// TUNEAMP_ACTIVE_ARTWORK_PREVIEW_V2
// TUNEAMP_UPLOAD_ARTWORK_PREVIEW_V1
// TUNEAMP_LYRICS_VIDEO_POLL_FIX_V2
// TUNEAMP_LYRICS_VIDEO_POLL_V1
// TUNEAMP_LYRICS_BUFFER_CONFIRM_V1
// TUNEAMP_LYRICS_BUFFER_V1
// TUNEAMP_MEDIA_ARTWORK_EDITOR_V1
// TUNEAMP_ACTIVE_ARTWORK_SOURCE_V1
// TUNEAMP_ARTWORK_FLOW_STATE_V1
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Calendar } from '@/components/ui/calendar';
import SongPicker from '@/components/SongPicker';
import AddSongDialog from '@/components/AddSongDialog';
import {
  Sparkle, TwitterLogo, InstagramLogo, TiktokLogo, FacebookLogo, YoutubeLogo, Copy, MusicNote,
  ImageSquare, VideoCamera, Download, PaperPlaneRight, CalendarBlank,
} from '@phosphor-icons/react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const PLATFORMS = [
  { id: 'twitter', name: 'Twitter/X', icon: TwitterLogo },
  { id: 'instagram', name: 'Instagram', icon: InstagramLogo },
  { id: 'tiktok', name: 'TikTok', icon: TiktokLogo },
  { id: 'facebook', name: 'Facebook', icon: FacebookLogo },
  { id: 'youtube', name: 'YouTube', icon: YoutubeLogo },
  { id: 'youtube_community', name: 'YouTube Community', icon: YoutubeLogo },
];


function extractSoundCloudEmbedSrc(embedHtml) {
  if (!embedHtml) return '';
  const match = embedHtml.match(/src=["']([^"']+)["']/i);
  return match?.[1] || '';
}

function formatTaxonomyLabel(value) {
  const special = {
    'afro': 'Afro',
    'hip-hop': 'Hip-Hop',
    'rnb-soul': 'R&B / Soul',
    'reggae-dancehall': 'Reggae / Dancehall',
    'soundtrack-cinematic': 'Soundtrack / Cinematic',
    'drum-and-bass': 'Drum & Bass',
    'liquid-dnb': 'Liquid DnB',
    'atmospheric-dnb': 'Atmospheric DnB',
    'minimal-dnb': 'Minimal DnB',
    'dancefloor-dnb': 'Dancefloor DnB',
    'tech-house': 'Tech House',
    'afro-house': 'Afro House',
    'melodic-house': 'Melodic House',
    'melodic-techno': 'Melodic Techno',
    'melodic-dubstep': 'Melodic Dubstep',
    'deep-house': 'Deep House',
    'deep-dubstep': 'Deep Dubstep',
    'uk-garage': 'UK Garage',
    '2-step': '2-Step',
    'acid-house': 'Acid House',
    'acid-techno': 'Acid Techno',
    'idm': 'IDM',
    'j-pop': 'J-Pop',
    'k-pop': 'K-Pop',
    'rnb': 'R&B',
    'neo-soul': 'Neo-Soul',
    'alt-pop': 'Alt-Pop',
    'alt-rock': 'Alt-Rock',
  };

  if (special[value]) return special[value];

  return value
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

const CREATE_POST_SELECTED_SONG_KEY =
  'tuneamplifier.createPost.selectedSongId';

export default function CreateAndPost() {
  const { refresh } = useAuth();
  const [songs, setSongs] = useState([]);
  const [songId, setSongId] = useState(() => {
    try {
      return sessionStorage.getItem(CREATE_POST_SELECTED_SONG_KEY) || '';
    } catch {
      return '';
    }
  });
  const [playlists, setPlaylists] = useState([]);
  const [playlistId, setPlaylistId] = useState('');
  const [contentType, setContentType] = useState('song');
  const [showAddPlaylist, setShowAddPlaylist] = useState(false);
  const [playlistLink, setPlaylistLink] = useState('');
  const [addingPlaylist, setAddingPlaylist] = useState(false);
  const [pricing, setPricing] = useState(null);

  // Step 1 — song
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({
    title: '',
    artist: '',
    genre: '',
    subgenre: '',
    microgenre: '',
    source_url: '',
    soundcloud_genre: '',
    soundcloud_embed_html: '',
    lyrics: '',
  });
  const [showLyricsAudioUpload, setShowLyricsAudioUpload] = useState(false);
  const [lyricsAudioFile, setLyricsAudioFile] = useState(null);
  const [uploadingLyricsAudio, setUploadingLyricsAudio] = useState(false);
  const [taxonomy, setTaxonomy] = useState({
    genres: [],
    subgenres_by_genre: {},
    microgenres_by_genre: {},
  });

  // Step 2 — captions
  const [platforms, setPlatforms] = useState(['twitter', 'instagram']);
  const [tone, setTone] = useState('energetic');
  const [aiModel, setAiModel] = useState('gpt-terra');
  const [genCaptions, setGenCaptions] = useState(false);
  const [captions, setCaptions] = useState(null);

  // Step 3 — media
  const [mediaKind, setMediaKind] = useState('image');
  const [imgModel, setImgModel] = useState('gpt-image-2.5-sunburst');
  const [imgQuality, setImgQuality] = useState('medium');
  const [mediaFormat, setMediaFormat] = useState('square');
  const [vidModel, setVidModel] = useState('sora');
  const [duration, setDuration] = useState(4);
  const [klingDuration, setKlingDuration] = useState(5);
  const [genMedia, setGenMedia] = useState(false);
  const [media, setMedia] = useState(null);
  const [mediaImagePromptMode, setMediaImagePromptMode] = useState('ai');
  const [mediaCustomPrompt, setMediaCustomPrompt] = useState('');
  const [lyricsImagePromptMode, setLyricsImagePromptMode] = useState('ai');
  const [lyricsCustomPrompt, setLyricsCustomPrompt] = useState('');
  const [mediaMode, setMediaMode] = useState('media');
  const [lyricsVideoFormat, setLyricsVideoFormat] = useState('landscape');
  const [lyricsVideoStyle, setLyricsVideoStyle] = useState('auto');
  const [lyricsArtworkFile, setLyricsArtworkFile] = useState(null);
  const [lyricsArtworkUrl, setLyricsArtworkUrl] = useState(null);
  const [lyricsArtworkPreviewUrl, setLyricsArtworkPreviewUrl] = useState(null);
  const [uploadingLyricsArtwork, setUploadingLyricsArtwork] = useState(false);
  const [genLyricsVideo, setGenLyricsVideo] = useState(false);

  // Lyrics artwork editor
  const [lyricsArtworkMode, setLyricsArtworkMode] = useState('fill');
  const [lyricsArtworkZoom, setLyricsArtworkZoom] = useState(1);
  const [lyricsArtworkPan, setLyricsArtworkPan] = useState({
    x: 0,
    y: 0,
  });
  const [lyricsArtworkNaturalSize, setLyricsArtworkNaturalSize] = useState({
    width: 1,
    height: 1,
  });
  const lyricsArtworkDragRef = useRef(null);

  // Unified artwork flow.
  const [communityArtworkMode, setCommunityArtworkMode] = useState('fill');
  const [communityArtworkZoom, setCommunityArtworkZoom] = useState(1);
  const [communityArtworkPan, setCommunityArtworkPan] = useState({
    x: 0,
    y: 0,
  });

  const [mediaArtworkSource, setMediaArtworkSource] = useState('generated');
  const [mediaArtworkMode, setMediaArtworkMode] = useState('fill');
  const [mediaArtworkZoom, setMediaArtworkZoom] = useState(1);
  const [mediaArtworkPan, setMediaArtworkPan] = useState({
    x: 0,
    y: 0,
  });
  const [mediaArtworkNaturalSize, setMediaArtworkNaturalSize] = useState({
    width: 1,
    height: 1,
  });
  const mediaArtworkDragRef = useRef(null);
  const [mediaArtworkPreviewUrl, setMediaArtworkPreviewUrl] = useState(null);

  const [lyricsArtworkSource, setLyricsArtworkSource] = useState(null);
  const [lyricsAiArtworkGenerating, setLyricsAiArtworkGenerating] = useState(false);
  const lyricsVideoPollRef = useRef(null);
  const [showEditSong, setShowEditSong] = useState(false);
  const [editSongId, setEditSongId] = useState('');
  const [editingSong, setEditingSong] = useState(false);
  const [fetchingLyrics, setFetchingLyrics] = useState(false);

  const [showEditPlaylist, setShowEditPlaylist] = useState(false);
  const [editPlaylistId, setEditPlaylistId] = useState('');
  const [editPlaylistForm, setEditPlaylistForm] = useState({
    name: '',
    source_url: '',
  });
  const [editingPlaylist, setEditingPlaylist] = useState(false);

  const [songSort, setSongSort] = useState('title');
  const [selectedSongIds, setSelectedSongIds] = useState([]);
  const [showRemoveSongs, setShowRemoveSongs] = useState(false);
  const [removingSongs, setRemovingSongs] = useState(false);
  const [refreshingPlaylist, setRefreshingPlaylist] = useState(false);
  const [fetchingPlaylistLyrics, setFetchingPlaylistLyrics] = useState(false);

  // Step 4 — select & post
  const [targets, setTargets] = useState([]);
  const [posting, setPosting] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [schedDate, setSchedDate] = useState(new Date());
  const [schedTime, setSchedTime] = useState('18:00');
  const [scheduling, setScheduling] = useState(false);
  const [youtubeFormat, setYoutubeFormat] = useState('video');

  // Step 5 — YouTube Community helper
  const [showYoutubeCommunityWizard, setShowYoutubeCommunityWizard] = useState(false);
  const [youtubeCommunityData, setYoutubeCommunityData] = useState(null);
  const [communityArtworkUrl, setCommunityArtworkUrl] = useState(null);
  const [communityArtworkSource, setCommunityArtworkSource] = useState(null);
  const [loadingCommunityArtwork, setLoadingCommunityArtwork] = useState(false);
  const [uploadingCommunityArtwork, setUploadingCommunityArtwork] = useState(false);
  const [manualPostContent, setManualPostContent] = useState(null);


  useEffect(() => {
    return () => {
      if (lyricsArtworkPreviewUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(lyricsArtworkPreviewUrl);
      }
    };
  }, [lyricsArtworkPreviewUrl]);

  useEffect(() => {
    return () => {
      if (mediaArtworkPreviewUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(mediaArtworkPreviewUrl);
      }
    };
  }, [mediaArtworkPreviewUrl]);

  const load = useCallback(async () => {
    const [songsRes, playlistsRes] = await Promise.all([
      api.get('/songs'),
      api.get('/playlists'),
    ]);

    const nextSongs = songsRes.data || [];
    const nextPlaylists = playlistsRes.data || [];

    setSongs(nextSongs);
    setPlaylists(nextPlaylists);

    let storedSongId = '';

    try {
      storedSongId =
        sessionStorage.getItem(CREATE_POST_SELECTED_SONG_KEY) || '';
    } catch {
      storedSongId = '';
    }

    const preferredSongId =
      storedSongId &&
      nextSongs.some(song => song.song_id === storedSongId)
        ? storedSongId
        : nextSongs[0]?.song_id || '';

    setSongId(preferredSongId);

    if (preferredSongId) {
      try {
        sessionStorage.setItem(
          CREATE_POST_SELECTED_SONG_KEY,
          preferredSongId
        );
      } catch {
        // Ignore storage failures.
      }
    }

    if (nextPlaylists.length) {
      setPlaylistId(prev => prev || nextPlaylists[0].playlist_id);
    }
  }, []);

  useEffect(() => {
    load();

    api.get('/pricing')
      .then(r => setPricing(r.data.ai_pricing))
      .catch(() => {});

    api.get('/labels/filters')
      .then(r => setTaxonomy(r.data || {
        genres: [],
        subgenres_by_genre: {},
        microgenres_by_genre: {},
      }))
      .catch(() => {});
  }, [load]);

  const addPlaylist = async () => {
    if (!playlistLink.trim()) {
      toast.error('Paste a playlist URL');
      return;
    }

    setAddingPlaylist(true);

    try {
      const { data } = await api.post('/playlists/import', {
        url: playlistLink.trim(),
      });

      await load();
      setContentType('playlist');
      setPlaylistId(data.playlist_id);
      setPlaylistLink('');
      setShowAddPlaylist(false);

      toast.success(`Playlist "${data.name}" added`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not import playlist');
    } finally {
      setAddingPlaylist(false);
    }
  };


  const openEditSong = (song) => {
    setEditSongId(song.song_id);
    setForm({
      title: song.title || '',
      artist: song.artist || '',
      genre: song.genre || '',
      subgenre: song.subgenre || '',
      microgenre: song.microgenre || '',
      source_url:
        song.source_url ||
        song.spotify_url ||
        song.youtube_url ||
        song.soundcloud_url ||
        '',
      soundcloud_genre: song.soundcloud_genre || '',
      soundcloud_embed_html: '',
      lyrics: song.lyrics || '',
    });
    setShowEditSong(true);
  };

  const fetchLyrics = async () => {
    if (!editSongId) return;

    setFetchingLyrics(true);

    try {
      const { data } = await api.post(`/songs/${editSongId}/fetch-lyrics`);
      setForm(prev => ({
        ...prev,
        lyrics: data.lyrics || '',
      }));
      toast.success(
        data.source
          ? `Lyrics fetched from ${data.source}`
          : 'Lyrics fetched'
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not fetch lyrics');
    } finally {
      setFetchingLyrics(false);
    }
  };

  const editSong = async () => {
    if (!editSongId) return;

    if (!form.title.trim()) {
      toast.error('Title required');
      return;
    }

    if (!form.artist.trim()) {
      toast.error('Artist required');
      return;
    }

    if (!form.genre) {
      toast.error('Choose a genre for this song');
      return;
    }

    setEditingSong(true);

    try {
      await api.patch(`/songs/${editSongId}`, {
        title: form.title,
        artist: form.artist,
        genre: form.genre,
        subgenre: form.subgenre || null,
        microgenre: form.microgenre || null,
        source_url: form.source_url?.trim() || null,
        soundcloud_genre: form.soundcloud_genre || null,
        lyrics: form.lyrics || null,
      });

      setShowEditSong(false);
      setEditSongId('');
      await load();
      setSongId(editSongId);

      toast.success('Song updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update song');
    } finally {
      setEditingSong(false);
    }
  };

  const openEditPlaylist = (playlist) => {
    setEditPlaylistId(playlist.playlist_id);
    setEditPlaylistForm({
      name: playlist.name || '',
      source_url:
        playlist.source_url ||
        playlist.spotify_url ||
        playlist.youtube_url ||
        '',
    });
    setShowEditPlaylist(true);
  };

  const editPlaylist = async () => {
    if (!editPlaylistId) return;

    if (!editPlaylistForm.name.trim()) {
      toast.error('Playlist name required');
      return;
    }

    setEditingPlaylist(true);

    try {
      await api.patch(`/playlists/${editPlaylistId}`, {
        name: editPlaylistForm.name,
        source_url: editPlaylistForm.source_url?.trim() || null,
      });

      setShowEditPlaylist(false);
      setEditPlaylistId('');
      await load();
      setPlaylistId(editPlaylistId);

      toast.success('Playlist updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update playlist');
    } finally {
      setEditingPlaylist(false);
    }
  };


  const deleteSong = async (song) => {
    const confirmed = window.confirm(`Remove "${song.title}" from your TuneAmplifier catalog?`);
    if (!confirmed) return;

    try {
      await api.delete(`/songs/${song.song_id}`);
      toast.success(`Removed "${song.title}"`);

      if (songId === song.song_id) {
        setSongId('');
        setCaptions(null);
        setTargets([]);
        setMedia(null);
      }

      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not remove song');
    }
  };

  const refreshPlaylist = async (playlist) => {
    if (!playlist) return;

    const confirmed = window.confirm(
      `Refresh "${playlist.name}" from the original playlist source?`
    );

    if (!confirmed) return;

    setRefreshingPlaylist(true);

    try {
      const { data } = await api.post(
        `/playlists/${playlist.playlist_id}/refresh`
      );

      await load();
      setPlaylistId(playlist.playlist_id);
      setSelectedSongIds([]);

      toast.success(
        `Playlist refreshed: ${data.added_count || 0} added, ${data.removed_count || 0} removed`
      );
    } catch (e) {
      toast.error(
        e.response?.data?.detail || 'Could not refresh playlist'
      );
    } finally {
      setRefreshingPlaylist(false);
    }
  };

  const getPlaylistLyrics = async (playlist) => {
    if (!playlist) return;

    setFetchingPlaylistLyrics(true);

    try {
      const { data } = await api.post(
        `/playlists/${playlist.playlist_id}/fetch-lyrics`
      );

      await load();
      setPlaylistId(playlist.playlist_id);

      toast.success(
        `Lyrics fetched: ${data.fetched_count || 0} added, ${data.skipped_count || 0} already had lyrics`
      );
    } catch (e) {
      toast.error(
        e.response?.data?.detail || 'Could not fetch playlist lyrics'
      );
    } finally {
      setFetchingPlaylistLyrics(false);
    }
  };

  const removeSelectedSongs = async () => {
    const ids = [...new Set(selectedSongIds)];

    if (!ids.length) {
      toast.error('Select at least one song');
      return;
    }

    const confirmed = window.confirm(
      `Remove ${ids.length} selected song${ids.length === 1 ? '' : 's'} from your catalog?`
    );

    if (!confirmed) return;

    setRemovingSongs(true);

    try {
      const { data } = await api.post('/songs/bulk-delete', {
        song_ids: ids,
      });

      if (ids.includes(songId)) {
        setSongId('');
        setCaptions(null);
        setTargets([]);
        setMedia(null);
      }

      setSelectedSongIds([]);
      setShowRemoveSongs(false);

      await load();

      toast.success(
        `Removed ${data.deleted_song_count || 0} song${(data.deleted_song_count || 0) === 1 ? '' : 's'}`
      );
    } catch (e) {
      toast.error(
        e.response?.data?.detail || 'Could not remove selected songs'
      );
    } finally {
      setRemovingSongs(false);
    }
  };

  const playlistNamesForSong = (id) =>
    playlists
      .filter(p => (p.song_ids || []).includes(id))
      .map(p => p.name || '')
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b))
      .join(' · ');

  const playlistMetaForSong = (id) => {
    const playlist = playlists.find(p => p.playlist_id === playlistId);

    if (!playlist) return null;

    const meta = (playlist.song_metadata || []).find(
      item => item.song_id === id
    );

    if (meta) return meta;

    const index = (playlist.song_ids || []).indexOf(id);

    return index >= 0
      ? { song_id: id, position: index }
      : null;
  };

  const sortedSongs = [...songs].sort((a, b) => {
    switch (songSort) {
      case 'playlist':
        return playlistNamesForSong(a.song_id).localeCompare(
          playlistNamesForSong(b.song_id),
          undefined,
          { sensitivity: 'base' }
        );

      case 'artist':
        return (a.artist || '').localeCompare(
          b.artist || '',
          undefined,
          { sensitivity: 'base' }
        );

      case 'release_date':
        return (b.release_date || '').localeCompare(
          a.release_date || ''
        );

      case 'added_at': {
        const am = playlistMetaForSong(a.song_id);
        const bm = playlistMetaForSong(b.song_id);

        return (bm?.added_at || '').localeCompare(
          am?.added_at || ''
        );
      }

      case 'playlist_order': {
        const am = playlistMetaForSong(a.song_id);
        const bm = playlistMetaForSong(b.song_id);

        return (
          (am?.position ?? 999999999) -
          (bm?.position ?? 999999999)
        );
      }

      case 'title':
      default:
        return (a.title || '').localeCompare(
          b.title || '',
          undefined,
          { sensitivity: 'base' }
        );
    }
  });

  const toggleSongSelection = (id) => {
    setSelectedSongIds(current =>
      current.includes(id)
        ? current.filter(x => x !== id)
        : [...current, id]
    );
  };

  const toggleAllSongSelection = () => {
    const ids = sortedSongs.map(song => song.song_id);

    if (
      ids.length &&
      ids.every(id => selectedSongIds.includes(id))
    ) {
      setSelectedSongIds([]);
    } else {
      setSelectedSongIds(ids);
    }
  };

  const togglePlatform = (p) => setPlatforms(s => s.includes(p) ? s.filter(x => x !== p) : [...s, p]);
  const toggleTarget = (p) => setTargets(s => s.includes(p) ? s.filter(x => x !== p) : [...s, p]);

  const generateCaptions = async () => {
    const contentId = contentType === 'song' ? songId : playlistId;

    if (!contentId) {
      toast.error(contentType === 'song' ? 'Pick a song first' : 'Pick a playlist first');
      return;
    }

    if (!platforms.length) {
      toast.error('Pick at least one platform');
      return;
    }

    setGenCaptions(true);
    setCaptions(null);
    setTargets([]);

    try {
      const payload = {
        platforms,
        tone,
        model: aiModel,
        ...(contentType === 'song'
          ? { song_id: songId }
          : { playlist_id: playlistId }),
      };

      const { data } = await api.post('/promo/generate', payload);

      setCaptions(data);
      setTargets(data.results?.map(r => r.platform.toLowerCase()) || []);

      toast.success(
        contentType === 'song'
          ? 'Song captions ready — one per platform.'
          : 'Playlist captions ready — one per platform.'
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Generation failed');
    } finally {
      setGenCaptions(false);
    }
  };


  const generateMedia = async () => {
    const contentId = contentType === 'song' ? songId : playlistId;

    if (!contentId) {
      toast.error(contentType === 'song' ? 'Pick a song first' : 'Pick a playlist first');
      return;
    }

    if (
      mediaKind === 'image' &&
      mediaImagePromptMode === 'custom' &&
      !mediaCustomPrompt.trim()
    ) {
      toast.error('Enter a custom artwork prompt first');
      return;
    }

    setGenMedia(true);
    setMedia(null);

    try {
      let data;

      if (contentType === 'song') {
        if (mediaKind === 'image') {
          const imagePayload = {
            song_id: songId,
            model: imgModel,
            quality: imgQuality,
            format: mediaFormat,
          };

          if (mediaImagePromptMode === 'custom') {
            imagePayload.prompt = mediaCustomPrompt.trim();
            imagePayload.custom_prompt = true;
            imagePayload.use_artwork_reference = false;
          }

          ({ data } = await api.post('/ai/image', imagePayload));
        } else if (vidModel === 'kling') {
          ({ data } = await api.post('/ai/video/kling', {
            song_id: songId,
            duration: klingDuration,
            format: mediaFormat,
          }));
        } else {
          ({ data } = await api.post('/ai/video', {
            song_id: songId,
            duration,
            format: mediaFormat,
          }));
        }
      } else {
        const playlist = playlists.find(p => p.playlist_id === playlistId);

        if (!playlist) {
          toast.error('Playlist not found');
          return;
        }

        const automaticPlaylistPrompt =
          `Create a compelling promotional visual for the Spotify playlist "${playlist.name}". ` +
          `${playlist.track_count || 0} tracks. ` +
          `Music playlist marketing artwork, premium modern composition, cinematic lighting, ` +
          `strong visual identity, suitable for social media promotion.`;

        const prompt =
          mediaImagePromptMode === 'custom'
            ? mediaCustomPrompt.trim()
            : automaticPlaylistPrompt;

        if (mediaKind === 'image') {
          ({ data } = await api.post('/ai/image', {
            prompt,
            model: imgModel,
            quality: imgQuality,
            format: mediaFormat,
          }));
        } else if (vidModel === 'kling') {
          ({ data } = await api.post('/ai/video/kling', {
            prompt,
            duration: klingDuration,
            format: mediaFormat,
          }));
        } else {
          ({ data } = await api.post('/ai/video', {
            prompt,
            duration,
            format: mediaFormat,
          }));
        }
      }

      setMedia({ ...data, kind: mediaKind });

      if (mediaKind === 'image') {
        setMediaArtworkPreviewUrl(`${BACKEND}${data.url}`);
        setMediaArtworkSource('generated');
        setMediaArtworkMode('fill');
        setMediaArtworkZoom(1);
        setMediaArtworkPan({ x: 0, y: 0 });
      }

      toast.success(
        `${mediaKind === 'image' ? 'Image' : 'Video'} ready · ${data.credits_charged} credits`
      );

      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Generation failed');
    } finally {
      setGenMedia(false);
    }
  };


  const captionFor = (p) => {
    const results = Array.isArray(captions?.results)
      ? captions.results
      : [];

    for (const item of results) {
      if (
        String(item?.platform || '').toLowerCase() ===
        String(p || '').toLowerCase()
      ) {
        return item;
      }
    }

    return null;
  };

  const selectedSong = (() => {
    const list = Array.isArray(songs) ? songs : [];

    for (const item of list) {
      if (
        String(item?.song_id || item?.id || '') ===
        String(songId || '')
      ) {
        return item;
      }
    }

    return null;
  })();

  const getSongPreviewSrc = (data) => {
    if (!data) return '';

    if (data.soundcloud_embed_html) {
      const match = data.soundcloud_embed_html.match(
        /src=["']([^"']+)["']/i
      );

      if (match?.[1]) {
        return match[1];
      }
    }

    if (data.soundcloud_url) {
      return `https://w.soundcloud.com/player/?url=${encodeURIComponent(
        data.soundcloud_url
      )}&color=%23ccff00&auto_play=false&hide_related=true&show_comments=false&show_user=true&show_reposts=false&visual=false`;
    }

    if (data.spotify_url) {
      const match = data.spotify_url.match(
        /open\.spotify\.com\/(track|album)\/([A-Za-z0-9]+)/
      );

      if (match) {
        return `https://open.spotify.com/embed/${match[1]}/${match[2]}?utm_source=tuneamplifier`;
      }
    }

    if (data.youtube_url) {
      const match = data.youtube_url.match(
        /(?:youtu\.be\/|youtube\.com\/watch\?v=)([A-Za-z0-9_-]+)/
      );

      if (match) {
        return `https://www.youtube.com/embed/${match[1]}`;
      }
    }

    return '';
  };

  const selectedSongPreviewSrc = getSongPreviewSrc(selectedSong);


  useEffect(() => {
    setCommunityArtworkUrl(null);
    setCommunityArtworkSource(null);
  }, [songId]);

  useEffect(() => {
    if (!songId) {
      setGenLyricsVideo(false);
      return undefined;
    }

    let cancelled = false;

    const stopPolling = () => {
      if (lyricsVideoPollRef.current) {
        clearInterval(lyricsVideoPollRef.current);
        lyricsVideoPollRef.current = null;
      }
    };

    const checkLyricsVideo = async () => {
      try {
        const { data } = await api.get(
          `/lyrics-video/latest?song_id=${encodeURIComponent(songId)}`
        );

        if (cancelled || !data) return;

        if (data.status === 'rendering') {
          setGenLyricsVideo(true);
          return;
        }

        if (data.status === 'ready' && data.url) {
          setMedia({
            ...data,
            kind: 'lyrics_video',
          });
          setGenLyricsVideo(false);
          stopPolling();
        }
      } catch (e) {
        if (e.response?.status === 404) {
          if (!genLyricsVideo) {
            setGenLyricsVideo(false);
            stopPolling();
          }
        }
      }
    };

    checkLyricsVideo();
    stopPolling();

    lyricsVideoPollRef.current = setInterval(
      checkLyricsVideo,
      2000
    );

    return () => {
      cancelled = true;
      stopPolling();
    };
  }, [songId, genLyricsVideo]);

  const songArtist =
    selectedSong?.artist ||
    selectedSong?.artist_name ||
    selectedSong?.artistName ||
    '';

  const songTitle =
    selectedSong?.title ||
    selectedSong?.name ||
    '';

  const youtubeTitle = [
    songArtist,
    songTitle,
    media?.kind === 'lyrics_video' ? '(Lyrics Video)' : '',
  ]
    .filter(Boolean)
    .join(' - ')
    .replace(' - (Lyrics Video)', ' (Lyrics Video)')
    .slice(0, 100);

  const activeGeneratedCaption = (() => {
    if (!Array.isArray(captions?.results)) {
      return null;
    }

    // YouTube Community is a publishing target, not a caption
    // generation platform. Prefer a caption from any normal
    // selected platform, otherwise use the first generated caption.
    let fallback = null;

    for (const target of targets) {
      if (target === 'youtube_community') continue;

      const candidate = captionFor(target);

      if (candidate) {
        return candidate;
      }
    }

    fallback = captions.results[0] || null;
    return fallback;
  })();

  const songLink =
    selectedSong?.source_url ||
    selectedSong?.spotify_url ||
    selectedSong?.youtube_url ||
    selectedSong?.soundcloud_url ||
    '';

  const currentPostContent = (() => {
    const baseText =
      manualPostContent !== null
        ? manualPostContent
        : activeGeneratedCaption?.caption || '';

    const trimmedText = String(baseText || '').trim();

    if (songLink && !trimmedText.includes(songLink)) {
      return [trimmedText, songLink]
        .filter(Boolean)
        .join('\n\n');
    }

    return trimmedText;
  })();


  const loadCommunityArtwork = async () => {
    if (!songId) {
      toast.error('Pick a song first');
      return;
    }

    setLoadingCommunityArtwork(true);

    try {
      const { data } = await api.post('/artwork/resolve', {
        song_id: songId,
      });

      if (!data?.url) {
        throw new Error('No artwork was found for this song');
      }

      setCommunityArtworkUrl(data.url);
      setCommunityArtworkSource('song_artwork');

      setMedia({
        url: data.url,
        kind: 'image',
        source: 'song_artwork',
        credits_charged: 0,
      });
      setMediaArtworkPreviewUrl(data.url);
      setMediaArtworkSource('song_artwork');
      setMediaArtworkMode('fill');
      setMediaArtworkZoom(1);
      setMediaArtworkPan({ x: 0, y: 0 });

      toast.success(
        'Highest-quality available song artwork loaded'
      );
    } catch (e) {
      toast.error(
        e.response?.data?.detail ||
        e.message ||
        'Could not load song artwork'
      );
    } finally {
      setLoadingCommunityArtwork(false);
    }
  };

  const uploadCommunityArtwork = async (file) => {
    if (!file) return;

    const isImage =
      file.type.startsWith('image/') ||
      /\.(png|jpe?g|webp|gif)$/i.test(file.name);

    if (!isImage) {
      toast.error('Artwork must be an image');
      return;
    }

    if (file.size > 25 * 1024 * 1024) {
      toast.error('Maximum artwork size is 25 MB');
      return;
    }

    setUploadingCommunityArtwork(true);

    try {
      const form = new FormData();
      form.append('file', file);

      const { data } = await api.post(
        '/lyrics-video/upload-artwork',
        form,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (!data?.url) {
        throw new Error('Artwork upload returned no URL');
      }

      const localPreviewUrl = URL.createObjectURL(file);
      setMediaArtworkPreviewUrl(localPreviewUrl);

      setCommunityArtworkUrl(data.url);
      setCommunityArtworkSource('uploaded_artwork');

      setMedia({
        url: data.url,
        preview_url: localPreviewUrl,
        kind: 'image',
        source: 'uploaded_artwork',
        credits_charged: 0,
      });
      setMediaArtworkSource('uploaded_artwork');
      setMediaArtworkMode('fill');
      setMediaArtworkZoom(1);
      setMediaArtworkPan({ x: 0, y: 0 });

      toast.success('Artwork uploaded and ready');
    } catch (e) {
      toast.error(
        e.response?.data?.detail ||
        e.message ||
        'Could not upload artwork'
      );
    } finally {
      setUploadingCommunityArtwork(false);
    }
  };

  const getYoutubeCommunityUrl = async () => {
    try {
      const { data } = await api.get('/social/providers');

      const providerValue = data?.providers;

      const providerList = Array.isArray(providerValue)
        ? providerValue
        : (
            providerValue &&
            typeof providerValue === 'object'
          )
          ? Object.values(providerValue)
          : [];

      let google = null;

      for (const provider of providerList) {
        if (
          String(provider?.id || '') === 'google' &&
          provider?.connected
        ) {
          google = provider;
          break;
        }
      }

      if (!google) {
        throw new Error(
          'YouTube is not connected. Connect YouTube first.'
        );
      }

      const handle = String(
        google.account?.handle ||
        google.account?.customUrl ||
        ''
      ).trim();

      if (handle) {
        const normalized = handle.startsWith('@')
          ? handle
          : `@${handle}`;

        return `https://www.youtube.com/${normalized}/posts`;
      }

      const channelId =
        google.account?.id ||
        google.account?.channel_id ||
        null;

      if (channelId) {
        return `https://www.youtube.com/channel/${channelId}/posts`;
      }

      throw new Error(
        'Could not determine the connected YouTube channel.'
      );
    } catch (e) {
      throw new Error(
        e.response?.data?.detail ||
        e.message ||
        'Could not determine the YouTube Community URL'
      );
    }
  };

  const downloadYoutubeCommunityArtwork = async () => {
    const url = String(
      youtubeCommunityData?.artwork_url ||
      communityArtworkUrl ||
      ''
    ).trim();

    if (!url) {
      toast.error(
        'Choose Use song artwork or Upload artwork first.'
      );
      return;
    }

    try {
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(
          `Artwork download failed: ${response.status}`
        );
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);

      const extension =
        blob.type === 'image/png'
          ? 'png'
          : blob.type === 'image/webp'
            ? 'webp'
            : 'jpg';

      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = `youtube-community-artwork.${extension}`;
      document.body.appendChild(a);
      a.click();
      a.remove();

      setTimeout(
        () => URL.revokeObjectURL(objectUrl),
        1000
      );

      toast.success(
        'Artwork downloaded. Upload it manually in YouTube.'
      );
    } catch (error) {
      console.warn(
        'Community artwork download failed:',
        error
      );

      toast.error(
        'Could not download the artwork.'
      );
    }
  };

  const copyYoutubeCommunityText = async () => {
    if (!youtubeCommunityData) {
      toast.error('Community post is not ready yet.');
      return;
    }

    const text = String(
      youtubeCommunityData.text || ''
    ).trim();

    if (!navigator.clipboard?.writeText) {
      toast.error(
        'Text clipboard is not supported by this browser.'
      );
      return;
    }

    try {
      await navigator.clipboard.writeText(text);

      toast.success(
        'Text copied. Return to YouTube and press Ctrl+V.'
      );
    } catch (error) {
      console.warn(
        'Community text clipboard failed',
        error
      );

      toast.error(
        'Could not copy the text to the clipboard.'
      );
    }
  };

  const getMediaArtworkFrameAspect = () => {
    if (mediaFormat === 'portrait') return 9 / 16;
    if (mediaFormat === 'landscape') return 16 / 9;
    return 1;
  };

  const getMediaArtworkRenderMetrics = () => {
    const iw = Math.max(1, mediaArtworkNaturalSize.width);
    const ih = Math.max(1, mediaArtworkNaturalSize.height);

    const frameAspect = getMediaArtworkFrameAspect();
    const frameW = 1;
    const frameH = 1 / frameAspect;

    const baseScale =
      mediaArtworkMode === 'fit'
        ? Math.min(frameW / iw, frameH / ih)
        : Math.max(frameW / iw, frameH / ih);

    const scale =
      baseScale * Number(mediaArtworkZoom || 1);

    const renderedW = iw * scale;
    const renderedH = ih * scale;

    const widthPct = renderedW / frameW * 100;
    const heightPct = renderedH / frameH * 100;

    const overflowXPct = Math.max(0, widthPct - 100);
    const overflowYPct = Math.max(0, heightPct - 100);

    const leftPct =
      (100 - widthPct) / 2 +
      Number(mediaArtworkPan.x || 0) * overflowXPct / 2;

    const topPct =
      (100 - heightPct) / 2 +
      Number(mediaArtworkPan.y || 0) * overflowYPct / 2;

    return {
      widthPct,
      heightPct,
      leftPct,
      topPct,
      overflowXPct,
      overflowYPct,
    };
  };

  const getMediaArtworkImageStyle = () => {
    const m = getMediaArtworkRenderMetrics();

    return {
      position: 'absolute',
      width: `${m.widthPct}%`,
      height: `${m.heightPct}%`,
      left: `${m.leftPct}%`,
      top: `${m.topPct}%`,
      maxWidth: 'none',
      maxHeight: 'none',
      objectFit: 'fill',
      userSelect: 'none',
      pointerEvents: 'none',
    };
  };

  const resetMediaArtwork = () => {
    setMediaArtworkMode('fit');
    setMediaArtworkZoom(1);
    setMediaArtworkPan({ x: 0, y: 0 });
  };

  const setMediaArtworkFit = () => {
    setMediaArtworkMode('fit');
    setMediaArtworkZoom(1);
    setMediaArtworkPan({ x: 0, y: 0 });
  };

  const setMediaArtworkFill = () => {
    setMediaArtworkMode('fill');
    setMediaArtworkZoom(1);
    setMediaArtworkPan({ x: 0, y: 0 });
  };

  const setMediaArtworkCrop = () => {
    setMediaArtworkMode('crop');
    setMediaArtworkZoom(1);
    setMediaArtworkPan({ x: 0, y: 0 });
  };

  const handleMediaArtworkPointerDown = (e) => {
    if (!media?.url || media.kind !== 'image' || genMedia) return;

    e.currentTarget.setPointerCapture?.(e.pointerId);

    mediaArtworkDragRef.current = {
      pointerId: e.pointerId,
      x: e.clientX,
      y: e.clientY,
    };
  };

  const handleMediaArtworkPointerMove = (e) => {
    const drag = mediaArtworkDragRef.current;

    if (!drag || drag.pointerId !== e.pointerId) return;

    const dx = e.clientX - drag.x;
    const dy = e.clientY - drag.y;

    drag.x = e.clientX;
    drag.y = e.clientY;

    const rect = e.currentTarget.getBoundingClientRect();
    const metrics = getMediaArtworkRenderMetrics();

    const overflowX =
      rect.width * Math.max(0, metrics.overflowXPct) / 100;

    const overflowY =
      rect.height * Math.max(0, metrics.overflowYPct) / 100;

    setMediaArtworkPan((prev) => ({
      x:
        overflowX > 0
          ? Math.max(
              -1,
              Math.min(
                1,
                prev.x + dx / (overflowX / 2)
              )
            )
          : prev.x,
      y:
        overflowY > 0
          ? Math.max(
              -1,
              Math.min(
                1,
                prev.y + dy / (overflowY / 2)
              )
            )
          : prev.y,
    }));
  };

  const handleMediaArtworkPointerUp = (e) => {
    const drag = mediaArtworkDragRef.current;

    if (drag && drag.pointerId === e.pointerId) {
      mediaArtworkDragRef.current = null;
    }

    try {
      e.currentTarget.releasePointerCapture?.(e.pointerId);
    } catch (_) {}
  };

  const getLyricsArtworkRenderMetrics = () => {
    const iw = Math.max(1, lyricsArtworkNaturalSize.width);
    const ih = Math.max(1, lyricsArtworkNaturalSize.height);

    const isPortrait = lyricsVideoFormat === 'portrait';
    const frameAspect = isPortrait ? 9 / 16 : 16 / 9;

    // Normalized frame: width = 1, height = 1 / aspect.
    const frameW = 1;
    const frameH = 1 / frameAspect;

    const baseScale =
      lyricsArtworkMode === 'fit'
        ? Math.min(frameW / iw, frameH / ih)
        : Math.max(frameW / iw, frameH / ih);

    const scale = baseScale * Number(lyricsArtworkZoom || 1);

    const renderedW = iw * scale;
    const renderedH = ih * scale;

    const widthPct = renderedW / frameW * 100;
    const heightPct = renderedH / frameH * 100;

    const overflowXPct = Math.max(0, widthPct - 100);
    const overflowYPct = Math.max(0, heightPct - 100);

    const leftPct =
      (100 - widthPct) / 2 +
      Number(lyricsArtworkPan.x || 0) * overflowXPct / 2;

    const topPct =
      (100 - heightPct) / 2 +
      Number(lyricsArtworkPan.y || 0) * overflowYPct / 2;

    return {
      widthPct,
      heightPct,
      leftPct,
      topPct,
      overflowXPct,
      overflowYPct,
    };
  };

  const getLyricsArtworkImageStyle = () => {
    const m = getLyricsArtworkRenderMetrics();

    return {
      position: 'absolute',
      width: `${m.widthPct}%`,
      height: `${m.heightPct}%`,
      left: `${m.leftPct}%`,
      top: `${m.topPct}%`,
      maxWidth: 'none',
      maxHeight: 'none',
      objectFit: 'fill',
      userSelect: 'none',
      pointerEvents: 'none',
    };
  };

  const resetLyricsArtwork = () => {
    setLyricsArtworkMode('fit');
    setLyricsArtworkZoom(1);
    setLyricsArtworkPan({ x: 0, y: 0 });
  };

  const setLyricsArtworkFit = () => {
    setLyricsArtworkMode('fit');
    setLyricsArtworkZoom(1);
    setLyricsArtworkPan({ x: 0, y: 0 });
  };

  const setLyricsArtworkFill = () => {
    setLyricsArtworkMode('fill');
    setLyricsArtworkZoom(1);
    setLyricsArtworkPan({ x: 0, y: 0 });
  };

  const setLyricsArtworkCrop = () => {
    setLyricsArtworkMode('crop');
    setLyricsArtworkZoom(1);
    setLyricsArtworkPan({ x: 0, y: 0 });
  };

  const handleLyricsArtworkPointerDown = (e) => {
    if (!lyricsArtworkPreviewUrl || genLyricsVideo) return;

    e.currentTarget.setPointerCapture?.(e.pointerId);

    lyricsArtworkDragRef.current = {
      pointerId: e.pointerId,
      x: e.clientX,
      y: e.clientY,
    };
  };

  const handleLyricsArtworkPointerMove = (e) => {
    const drag = lyricsArtworkDragRef.current;

    if (!drag || drag.pointerId !== e.pointerId) return;

    const dx = e.clientX - drag.x;
    const dy = e.clientY - drag.y;

    drag.x = e.clientX;
    drag.y = e.clientY;

    const rect = e.currentTarget.getBoundingClientRect();
    const metrics = getLyricsArtworkRenderMetrics();

    const overflowX =
      rect.width * Math.max(0, metrics.overflowXPct) / 100;
    const overflowY =
      rect.height * Math.max(0, metrics.overflowYPct) / 100;

    setLyricsArtworkPan((prev) => ({
      x:
        overflowX > 0
          ? Math.max(
              -1,
              Math.min(
                1,
                prev.x + dx / (overflowX / 2)
              )
            )
          : prev.x,
      y:
        overflowY > 0
          ? Math.max(
              -1,
              Math.min(
                1,
                prev.y + dy / (overflowY / 2)
              )
            )
          : prev.y,
    }));
  };

  const handleLyricsArtworkPointerUp = (e) => {
    const drag = lyricsArtworkDragRef.current;

    if (drag && drag.pointerId === e.pointerId) {
      lyricsArtworkDragRef.current = null;
    }

    try {
      e.currentTarget.releasePointerCapture?.(e.pointerId);
    } catch (_) {}
  };

  const generateLyricsArtwork = async () => {
    if (!songId) {
      toast.error('Pick a song first');
      return;
    }

    if (
      lyricsImagePromptMode === 'custom' &&
      !lyricsCustomPrompt.trim()
    ) {
      toast.error('Enter a custom artwork prompt first');
      return;
    }

    try {
      const { data: latest } = await api.get(
        `/ai/image/latest?song_id=${encodeURIComponent(songId)}`
      );

      if (latest?.url) {
        const replaceExisting = window.confirm(
          'A temporary AI artwork was generated within the last 15 minutes.\n\nOK = replace the existing artwork\nCancel = keep the existing artwork.'
        );

        if (!replaceExisting) {
          setLyricsArtworkFile(null);
          setLyricsArtworkUrl(latest.url);
          setLyricsArtworkPreviewUrl(latest.url);
          setLyricsArtworkSource('ai_generated');
          setLyricsArtworkMode('fill');
          setLyricsArtworkZoom(1);
          setLyricsArtworkPan({ x: 0, y: 0 });
          toast.success('Existing AI artwork kept');
          return;
        }
      }
    } catch (e) {
      if (e.response?.status !== 404) {
        toast.error(
          e.response?.data?.detail ||
          'Could not check the temporary AI artwork'
        );
        return;
      }
    }

    setLyricsAiArtworkGenerating(true);

    try {
      const imagePayload = {
        model: 'gpt-image-2.5-sunburst',
        quality: 'medium',
        format: lyricsVideoFormat,
        song_id: songId,
        use_artwork_reference: false,
        temporary_buffer: true,
      };

      if (lyricsImagePromptMode === 'custom') {
        imagePayload.prompt = lyricsCustomPrompt.trim();
        imagePayload.custom_prompt = true;
        imagePayload.use_artwork_reference = false;
      } else {
        imagePayload.prompt = [
          'Create original premium single/album artwork for this song, specifically designed as a cinematic visual foundation for a lyrics video.',
          'Think like a world-class music photographer, film director and album art director working together.',
          'Create one powerful, memorable visual concept derived from the song title, lyrics, artist identity, genre and emotional atmosphere.',
          'The image should feel like a frame from a high-budget feature film or a premium editorial music campaign, not an ordinary photograph.',
          'Use dramatic cinematic lighting, motivated practical light sources, volumetric atmosphere, rich shadows, controlled highlights, sophisticated color grading, realistic textures, cinematic depth of field and premium photographic detail.',
          'Use strong visual storytelling, intentional framing, foreground/midground/background separation and a distinctive hero subject.',
          'Prefer unusual, visually striking environments, symbolic details and strong composition over ordinary everyday scenes.',
          'Avoid generic lifestyle photography, generic parties, generic concerts, generic DJs, stock-photo aesthetics, tourist-photo aesthetics, flat AI compositions and obvious genre clichés.',
          'Do not simply illustrate the literal lyrics; interpret them artistically and create a memorable visual metaphor when appropriate.',
          'Photorealism must be cinematic and polished, with believable anatomy, natural skin, realistic materials and physically believable light.',
          'No text, no typography, no letters, no logos, no watermarks and no poster design.',
          'Leave a visually calm but aesthetically integrated area around the center for animated lyrics, without making the entire center empty.',
          'The final image must look like finished premium music artwork even without any typography.',
        ].join(' ');
      }

      const { data } = await api.post('/ai/image', imagePayload);

      if (!data?.url) {
        throw new Error('AI artwork generation returned no image');
      }

      setLyricsArtworkFile(null);
      setLyricsArtworkUrl(data.url);
      setLyricsArtworkPreviewUrl(data.url);
      setLyricsArtworkSource('ai_generated');
      setLyricsArtworkMode('fill');
      setLyricsArtworkZoom(1);
      setLyricsArtworkPan({ x: 0, y: 0 });

      toast.success(
        `${data.width || ''}${data.width ? '×' : ''}${data.height || ''} AI artwork ready`
      );
    } catch (e) {
      toast.error(
        e.response?.data?.detail ||
        e.message ||
        'Could not generate AI artwork'
      );
    } finally {
      setLyricsAiArtworkGenerating(false);
    }
  };

  const useSongArtworkForLyricsVideo = async () => {
    if (!songId) {
      toast.error('Pick a song first');
      return;
    }

    try {
      const { data } = await api.post('/artwork/resolve', {
        song_id: songId,
      });

      if (!data?.url) {
        throw new Error('No artwork was found for this song');
      }

      setLyricsArtworkFile(null);
      setLyricsArtworkUrl(data.url);
      setLyricsArtworkPreviewUrl(data.url);
      setLyricsArtworkSource('song_artwork');
      setLyricsArtworkMode('fill');
      setLyricsArtworkZoom(1);
      setLyricsArtworkPan({ x: 0, y: 0 });

      toast.success('Song artwork loaded');
    } catch (e) {
      toast.error(
        e.response?.data?.detail ||
        e.message ||
        'Could not load song artwork'
      );
    }
  };

  const uploadLyricsArtwork = async (file) => {
    if (!file) return;

    const localPreviewUrl = URL.createObjectURL(file);

    setLyricsArtworkFile(file);
    setLyricsArtworkPreviewUrl(localPreviewUrl);
    setLyricsArtworkSource('uploaded_artwork');
    setLyricsArtworkMode('fill');
    setLyricsArtworkZoom(1);
    setLyricsArtworkPan({ x: 0, y: 0 });
    setUploadingLyricsArtwork(true);

    try {
      const form = new FormData();
      form.append('file', file);

      const { data } = await api.post(
        '/lyrics-video/upload-artwork',
        form,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      setLyricsArtworkUrl(data.url);
      toast.success('Artwork uploaded temporarily');

    } catch (e) {
      setLyricsArtworkFile(null);
      setLyricsArtworkUrl(null);
      setLyricsArtworkPreviewUrl(null);
      setLyricsArtworkSource(null);
      toast.error(
        e.response?.data?.detail ||
        'Could not upload artwork'
      );
    } finally {
      setUploadingLyricsArtwork(false);
    }
  };

  const runLyricsVideoGeneration = async (song) => {
    if (!song?.lyrics?.trim()) {
      toast.error('Add lyrics to this song first');
      return;
    }

    if (!song?.audio_url) {
      setShowLyricsAudioUpload(true);
      return;
    }

    setGenLyricsVideo(true);

    try {
      let backgroundUrl = lyricsArtworkUrl || null;

      const { data } = await api.post('/lyrics-video/render', {
        song_id: songId,
        format: lyricsVideoFormat,
        background_url: backgroundUrl,
        style: lyricsVideoStyle,
        artwork_mode: lyricsArtworkMode,
        artwork_zoom: Number(lyricsArtworkZoom || 1),
        artwork_pan_x: Number(lyricsArtworkPan.x || 0),
        artwork_pan_y: Number(lyricsArtworkPan.y || 0),
      });

      setMedia({
        ...data,
        kind: 'lyrics_video',
      });

      toast.success(
        `Lyrics video ready · ${data.width}×${data.height} · ${Number(data.credits_charged ?? 2).toFixed(1)} credits`
      );

      await Promise.all([load(), refresh()]);
    } catch (e) {
      toast.error(
        e.response?.data?.detail || 'Lyrics video generation failed'
      );
    } finally {
      setGenLyricsVideo(false);
    }
  };

  const uploadLyricsAudioAndGenerate = async () => {
    if (!songId) {
      toast.error('Pick a song first');
      return;
    }

    if (!lyricsAudioFile) {
      toast.error('Choose an MP3 file first');
      return;
    }

    const isMp3 =
      lyricsAudioFile.type === 'audio/mpeg' ||
      lyricsAudioFile.type === 'audio/mp3' ||
      lyricsAudioFile.name.toLowerCase().endsWith('.mp3');

    if (!isMp3) {
      toast.error('Lyrics video audio must be an MP3 file');
      return;
    }

    if (lyricsAudioFile.size > 250 * 1024 * 1024) {
      toast.error('Maximum audio file size is 250 MB');
      return;
    }

    setUploadingLyricsAudio(true);

    try {
      const fd = new FormData();
      fd.append('file', lyricsAudioFile);

      await api.post(`/songs/${songId}/audio`, fd);

      const { data: freshSongs } = await api.get('/songs');
      setSongs(freshSongs || []);

      const updatedSong = (freshSongs || []).find(
        s => s.song_id === songId
      );

      if (!updatedSong?.audio_url) {
        throw new Error('MP3 upload did not complete');
      }

      setLyricsAudioFile(null);
      setShowLyricsAudioUpload(false);

      await runLyricsVideoGeneration(updatedSong);
    } catch (e) {
      toast.error(
        e.response?.data?.detail || 'Could not upload MP3'
      );
    } finally {
      setUploadingLyricsAudio(false);
    }
  };

  const generateLyricsVideo = async () => {
    if (!songId) {
      toast.error('Pick a song first');
      return;
    }

    const song = songs.find(s => s.song_id === songId);

    if (!song) {
      toast.error('Song not found');
      return;
    }

    if (!song?.lyrics?.trim()) {
      toast.error('Add lyrics to this song first');
      return;
    }

    if (!song?.audio_url) {
      setLyricsAudioFile(null);
      setShowLyricsAudioUpload(true);
      return;
    }

    let existingLyricsVideo = null;

    try {
      const { data } = await api.get(
        `/lyrics-video/latest?song_id=${encodeURIComponent(songId)}`
      );

      existingLyricsVideo = data || null;
    } catch (e) {
      if (e.response?.status !== 404) {
        toast.error(
          e.response?.data?.detail ||
          'Could not check the existing lyrics video'
        );
        return;
      }
    }

    if (existingLyricsVideo?.status === 'rendering') {
      setGenLyricsVideo(true);
      return;
    }

    if (existingLyricsVideo?.status === 'ready' && existingLyricsVideo?.url) {
      const keepExisting = window.confirm(
        'A Lyrics Video already exists in the buffer.\n\nOK = keep the existing video\nCancel = generate a new video and replace it.'
      );

      if (keepExisting) {
        setMedia({
          ...existingLyricsVideo,
          kind: 'lyrics_video',
        });
        setGenLyricsVideo(false);
        return;
      }
    }

    await runLyricsVideoGeneration(song);
  };

  const lyricsAudioUploadDialog = (
    <Dialog
      open={showLyricsAudioUpload}
      onOpenChange={(open) => {
        if (!uploadingLyricsAudio) {
          setShowLyricsAudioUpload(open);
          if (!open) setLyricsAudioFile(null);
        }
      }}
    >
      <DialogContent className="bg-zinc-950 border-zinc-800">
        <DialogHeader>
          <DialogTitle className="font-display">
            Upload MP3 for lyrics video
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-4">
            <p className="text-sm text-zinc-300">
              Upload the MP3 used for this lyrics video.
            </p>
            <p className="mt-2 text-xs text-zinc-500">
              Temporary audio. It is automatically deleted after 15 minutes.
              You can generate multiple lyrics videos during that time.
            </p>
          </div>

          <Input
            type="file"
            accept=".mp3,audio/mpeg,audio/mp3"
            disabled={uploadingLyricsAudio}
            onChange={(e) => {
              setLyricsAudioFile(e.target.files?.[0] || null);
            }}
            className="bg-zinc-950 border-zinc-800"
          />

          {lyricsAudioFile && (
            <div className="flex items-center gap-2 text-xs font-mono text-zinc-400">
              <MusicNote size={13} className="text-[#ccff00]" />
              <span className="truncate">{lyricsAudioFile.name}</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <div className="flex items-center justify-end gap-2 w-full">
            <Button
              type="button"
              variant="outline"
              disabled={uploadingLyricsAudio}
              onClick={() => {
                setShowLyricsAudioUpload(false);
                setLyricsAudioFile(null);
              }}
              className="border-zinc-800"
            >
              Cancel
            </Button>

            <Button
              type="button"
              disabled={uploadingLyricsAudio || !lyricsAudioFile}
              onClick={uploadLyricsAudioAndGenerate}
              className="btn-neon rounded-full"
            >
              {uploadingLyricsAudio
                ? 'Uploading & generating…'
                : 'Upload & generate'}
            </Button>
          </div>

          <p className="w-full text-[11px] text-zinc-600 text-right mt-1">
            Cost: 2 credits
          </p>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  const downloadLyricsVideo = async () => {
    if (!media?.url || media?.kind !== 'lyrics_video') return;

    try {
      const filename = media.url.split('/').pop();
      const response = await api.get(
        `/lyrics-video/${encodeURIComponent(filename)}/download`,
        { responseType: 'blob' }
      );

      const blobUrl = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename || 'lyrics-video.mp4';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);

      toast.success('Download started. Temporary server files will be cleaned up shortly.');
    } catch (e) {
      toast.error(
        e.response?.data?.detail || 'Could not download lyrics video'
      );
    }
  };

  // TikTok returns a publish_id before the post is actually complete.
  // Keep checking the official status endpoint and only treat PUBLISH_COMPLETE
  // as a real successful post.
  // Platforms such as TikTok, Facebook and YouTube can accept an
  // upload before the final publication/processing step is complete.
  // Only PUBLISHED is treated as a real successful post.
  // TUNEAMP_TIKTOK_FAILURE_MESSAGES
  const formatPublishingFailure = (data) => {
    const reason =
      data?.fail_reason ||
      data?.platform_meta?.fail_reason ||
      data?.platform_meta?.error ||
      '';

    const messages = {
      video_pull_failed:
        'TikTok could not download the video from TuneAmplifier.',
      file_format_check_failed:
        'TikTok rejected the generated video file format.',
      duration_check_failed:
        'TikTok rejected the video because its duration exceeds the allowed limit.',
      picture_size_check_failed:
        'TikTok rejected the video dimensions.',
      frame_rate_check_failed:
        'TikTok rejected the video frame rate.',
      file_size_check_failed:
        'TikTok rejected the video because the file size is too large.',
      path_check_failed:
        'TikTok could not access the media file URL.',
      photo_size_check_failed:
        'TikTok rejected the image dimensions.',
    };

    if (reason) {
      return `${messages[reason] || `TikTok rejected the media (${reason}).`}`;
    }

    return data?.message || 'Publishing failed.';
  };

  const waitForPublishingHistory = async (
    historyId,
    maxWaitMs = 13 * 60 * 1000
  ) => {
    const startedAt = Date.now();
    const intervalMs = 6000;
    let last = null;

    while (Date.now() - startedAt < maxWaitMs) {
      try {
        const { data } = await api.post(
          `/social/publish/history/${encodeURIComponent(historyId)}/refresh`
        );

        last = data;

        if (data?.status === 'PUBLISHED') {
          return {
            verified: true,
            data,
          };
        }

        if (data?.status === 'FAILED') {
          return {
            verified: false,
            data,
            failReason: formatPublishingFailure(data),
          };
        }
      } catch (e) {
        const status = e.response?.status;

        // Retry transient API/rate-limit/server errors.
        if (!status || status >= 500 || status === 429) {
          await new Promise(resolve => setTimeout(resolve, intervalMs));
          continue;
        }

        return {
          verified: false,
          data: last,
          failReason:
            e.response?.data?.detail ||
            e.message ||
            'Could not verify publishing status.',
        };
      }

      await new Promise(resolve => setTimeout(resolve, intervalMs));
    }

    return {
      verified: false,
      data: last,
      failReason:
        'Publishing did not complete within the verification window.',
      timedOut: true,
    };
  };

  const postNow = async () => {
    if (!targets.length) {
      toast.error('Select at least one platform to post to');
      return;
    }

    const communitySelected = targets.includes('youtube_community');
    const socialTargets = targets.filter(
      t => t !== 'youtube_community'
    );

    const effectiveSocialMediaUrl =
      communityArtworkUrl || media?.url || null;

    if (socialTargets.length && !effectiveSocialMediaUrl) {
      toast.error(
        'There is nothing to post. Generate a photo/video or choose an artwork source first.'
      );
      return;
    }

    if (
      socialTargets.includes('youtube') &&
      (!media?.url || !['video', 'lyrics_video'].includes(media?.kind))
    ) {
      toast.error(
        'YouTube video publishing requires a generated or uploaded video.'
      );
      return;
    }

    let communityWindow = null;

    // Open the popup immediately while this click is still a
    // user gesture, so the browser is less likely to block it.
    if (communitySelected) {
      const popupWidth = Math.max(
        760,
        Math.floor(window.screen.availWidth * 0.52)
      );

      const popupHeight = Math.max(
        700,
        Math.floor(window.screen.availHeight * 0.90)
      );

      const popupLeft = Math.floor(
        window.screen.availLeft +
        window.screen.availWidth -
        popupWidth -
        20
      );

      const popupTop = Math.floor(
        window.screen.availTop +
        Math.max(
          20,
          (window.screen.availHeight - popupHeight) / 2
        )
      );

      communityWindow = window.open(
        'about:blank',
        'tuneamplifier-youtube-community',
        `popup=yes,width=${popupWidth},height=${popupHeight},left=${popupLeft},top=${popupTop},resizable=yes,scrollbars=yes`
      );

      if (communityWindow) {
        try {
          communityWindow.document.title =
            'TuneAmplifier — YouTube Community';
        } catch (e) {
          // Ignore cross-window/document quirks.
        }
      }
    }

    setPosting(true);

    let okCount = 0;
    let attemptedCount = 0;
    const pendingAsyncPublishes = [];

    try {
      // --------------------------------------------------------
      // A. Normal social platforms + YouTube video
      // --------------------------------------------------------

      for (const t of socialTargets) {
        attemptedCount++;

        const publishToastId = toast.loading(
          `${t}: Uploading...`,
          { duration: Infinity }
        );

        try {
          const { data } = await api.post('/social/publish', {
            targets: [t],
            content: currentPostContent.trim(),
            hashtags: [],
            media_url:
              t === 'youtube'
                ? media?.url || undefined
                : effectiveSocialMediaUrl || undefined,

            youtube_format:
              t === 'youtube' ? youtubeFormat : undefined,
            song_id: songId,
            media_kind: media?.kind || mediaKind,
            youtube_title:
              t === 'youtube' ? youtubeTitle : undefined,
          });

          const r = data.results?.[0];

          if (r?.ok) {
            const isAsyncPlatform =
              t === 'tiktok' ||
              t === 'facebook' ||
              t === 'youtube';

            if (isAsyncPlatform) {
              if (r.history_id) {
                pendingAsyncPublishes.push({
                  target: t,
                  historyId: r.history_id,
                  note: r.note,
                  toastId: publishToastId,
                });

                toast.loading(
                  `${t}: Processing...`,
                  {
                    id: publishToastId,
                    duration: Infinity,
                  }
                );
              } else {
                toast.error(
                  `${t}: Upload accepted, but TuneAmplifier could not verify the publishing operation.`,
                  {
                    id: publishToastId,
                    duration: 8000,
                  }
                );
              }
            } else {
              okCount++;

              toast.success(
                `Posted to ${t}${r.note ? ' — ' + r.note : ''}`,
                {
                  id: publishToastId,
                  duration: 5000,
                }
              );
            }
          } else {
            toast.error(
              `${t}: ${r?.error || 'failed'}`,
              {
                id: publishToastId,
                duration: 8000,
              }
            );
          }
        } catch (e) {
          toast.error(
            `${t}: ${e.response?.data?.detail || 'failed'}`,
            {
              id: publishToastId,
              duration: 8000,
            }
          );
        }
      }

      // --------------------------------------------------------
      // B. Verify asynchronous publishing before declaring success
      //    or cleaning up the lyrics-video source file.
      // --------------------------------------------------------

      for (const pending of pendingAsyncPublishes) {
        try {
          const verified = await waitForPublishingHistory(
            pending.historyId
          );

          if (verified.verified) {
            okCount++;

            toast.success(
              `Posted to ${pending.target}${pending.note ? ' — ' + pending.note : ''}`,
              {
                id: pending.toastId,
                duration: 5000,
              }
            );
          } else {
            toast.error(
              `${pending.target}: ${
                verified.failReason ||
                'Publishing did not complete.'
              }`,
              {
                id: pending.toastId,
                duration: 8000,
              }
            );
          }
        } catch (e) {
          toast.error(
            `${pending.target} verification failed: ${
              e.response?.data?.detail ||
              e.message ||
              'could not verify publishing status'
            }`,
            {
              id: pending.toastId,
              duration: 8000,
            }
          );
        }
      }

      // --------------------------------------------------------
      // B. Lyrics-video cleanup after actual API posts only
      // --------------------------------------------------------

      if (
        media?.kind === 'lyrics_video' &&
        okCount === attemptedCount &&
        attemptedCount > 0
      ) {
        try {
          const filename = media.url.split('/').pop();

          await api.post(
            `/lyrics-video/${encodeURIComponent(filename)}/mark-posted`
          );

          toast.success(
            'Temporary lyrics video marked for cleanup.'
          );
        } catch (e) {
          console.warn(
            'Could not mark lyrics video as posted',
            e
          );
        }
      }

      // --------------------------------------------------------
      // C. YouTube Community
      // --------------------------------------------------------

      if (communitySelected) {
        try {
          const communityUrl =
            await getYoutubeCommunityUrl();

          const songLink =
            selectedSong?.source_url ||
            selectedSong?.spotify_url ||
            selectedSong?.youtube_url ||
            selectedSong?.soundcloud_url ||
            '';

          const baseText =
            String(currentPostContent || '').trim();

          const communityText = [
            baseText,
            songLink && !baseText.includes(songLink)
              ? songLink
              : '',
          ]
            .filter(Boolean)
            .join('\n\n')
            .trim();

          const artworkUrl =
            communityArtworkUrl ||
            (media?.kind === 'image'
              ? media.url
              : null) ||
            lyricsArtworkUrl ||
            selectedSong?.cover_url ||
            null;

          const data = {
            url: communityUrl,
            text: communityText,
            artwork_url: artworkUrl,
            song_link: songLink,
          };

          setYoutubeCommunityData(data);

          if (communityWindow && !communityWindow.closed) {
            communityWindow.location.href = communityUrl;

            try {
              communityWindow.focus();
            } catch (e) {
              // Ignore focus restrictions.
            }
          } else {
            // Popup was blocked: open normally as a fallback.
            window.open(
              communityUrl,
              '_blank',
              'width=1450,height=950,resizable=yes,scrollbars=yes'
            );
          }

          setShowYoutubeCommunityWizard(true);
        } catch (e) {
          if (communityWindow && !communityWindow.closed) {
            try {
              communityWindow.close();
            } catch (closeError) {
              // Ignore.
            }
          }

          toast.error(
            e.message ||
            e.response?.data?.detail ||
            'Could not prepare YouTube Community'
          );
        }
      }
    } finally {
      setPosting(false);
    }

    if (okCount > 0) {
      toast.success(
        `${okCount} post${okCount > 1 ? 's' : ''} live.`
      );
    }
  };

  const schedulePosts = async () => {
    if (!targets.length) { toast.error('Select at least one platform'); return; }
    const [h, m] = schedTime.split(':');
    const dt = new Date(schedDate); dt.setHours(+h, +m, 0, 0);
    setScheduling(true);
    try {
      for (const t of targets) {
        const c = captionFor(t);
        if (!c) continue;
        await api.post('/scheduler/posts', {
          platform: t, content: c.caption, hashtags: c.hashtags || [], song_id: songId,
          media_url: media?.url || undefined, scheduled_at: dt.toISOString(),
        });
      }
      toast.success(`Queued for ${dt.toLocaleString()}`);
      setShowSchedule(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not queue'); }
    finally { setScheduling(false); }
  };

  return (
    <div className="p-6 lg:p-8 max-w-[1400px]">
      <header className="mb-8">
        <div className="font-mono text-xs text-zinc-500 uppercase tracking-wider">AI Social Media Posting</div>
        <h1 className="font-display text-4xl lg:text-5xl font-bold mt-1">Create &amp; Post.</h1>
        <p className="text-zinc-400 mt-1 text-sm max-w-2xl">
          Pick a song, generate native captions per platform, add a photo or video, then post now or schedule — all in one place.
        </p>
      </header>

      <div className="space-y-6">
        {/* Step 1 — content */}
        <Step n={1} title="Pick content to post">
          <div className="space-y-4">
            <div className="flex gap-2">
              <Button
                type="button"
                onClick={() => setContentType('song')}
                className={`rounded-full ${contentType === 'song' ? 'btn-neon' : 'border border-zinc-800 bg-transparent text-zinc-400'}`}
              >
                Song
              </Button>
              <Button
                type="button"
                onClick={() => setContentType('playlist')}
                className={`rounded-full ${contentType === 'playlist' ? 'btn-neon' : 'border border-zinc-800 bg-transparent text-zinc-400'}`}
              >
                Playlist
              </Button>
            </div>

            {contentType === 'song' ? (
              <div className="flex gap-2 items-center flex-wrap">
                <SongPicker
                  songs={songs}
                  playlists={playlists}
                  selectedSongId={songId}
                  onSongChange={(value) => {
                    const nextSongId = value || '';

                    setSongId(nextSongId);

                    try {
                      if (nextSongId) {
                        sessionStorage.setItem(
                          CREATE_POST_SELECTED_SONG_KEY,
                          nextSongId
                        );
                      } else {
                        sessionStorage.removeItem(
                          CREATE_POST_SELECTED_SONG_KEY
                        );
                      }
                    } catch {
                      // Ignore storage failures.
                    }
                  }}
                  playlistId={playlistId}
                  songSort={songSort}
                  onSongSortChange={setSongSort}
                  onAddSong={() => setShowAdd(true)}
                  onEditSong={openEditSong}
                  onRemoveSong={deleteSong}
                  onRemoveMultiple={() => setShowRemoveSongs(true)}
                />

                                <Dialog
                  open={showRemoveSongs}
                  onOpenChange={setShowRemoveSongs}
                >
                  <DialogContent className="bg-zinc-950 border-zinc-800 max-w-2xl">
                    <DialogHeader>
                      <DialogTitle className="font-display">
                        Remove multiple songs
                      </DialogTitle>
                    </DialogHeader>

                    <div className="space-y-3 max-h-[55vh] overflow-y-auto pr-1">
                      <div className="flex items-center justify-between">
                        <div className="text-xs text-zinc-500">
                          {selectedSongIds.length} selected
                        </div>

                        <div className="flex gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={toggleAllSongSelection}
                            className="border-zinc-800"
                          >
                            Select all
                          </Button>

                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setSelectedSongIds([])}
                            className="border-zinc-800"
                          >
                            Clear
                          </Button>
                        </div>
                      </div>

                      {sortedSongs.map(song => (
                        <label
                          key={song.song_id}
                          className="flex items-center gap-3 rounded-lg border border-zinc-800 px-3 py-2 cursor-pointer hover:border-zinc-700"
                        >
                          <input
                            type="checkbox"
                            checked={selectedSongIds.includes(song.song_id)}
                            onChange={() => toggleSongSelection(song.song_id)}
                            className="accent-[#ccff00]"
                          />

                          <div className="min-w-0">
                            <div className="text-sm truncate">
                              {song.title}
                            </div>
                            <div className="text-xs text-zinc-500 truncate">
                              {song.artist}
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>

                    <DialogFooter>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setShowRemoveSongs(false)}
                        className="border-zinc-800"
                      >
                        Cancel
                      </Button>

                      <Button
                        type="button"
                        onClick={removeSelectedSongs}
                        disabled={removingSongs || !selectedSongIds.length}
                        className="bg-red-600 hover:bg-red-500 text-white"
                      >
                        {removingSongs
                          ? 'Removing…'
                          : `Remove ${selectedSongIds.length}`}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>

                <AddSongDialog
                  open={showAdd}
                  onOpenChange={setShowAdd}
                  onSaved={async savedSong => {
                    await load();
                    setSongId(savedSong.song_id);
                  }}
                />

                <Dialog open={showEditSong} onOpenChange={setShowEditSong}>
                  <DialogContent className="bg-zinc-950 border-zinc-800 max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="font-display">Edit song</DialogTitle>
                    </DialogHeader>

                    <div className="space-y-3">
                      <Input
                        placeholder="Title"
                        value={form.title}
                        onChange={e => setForm({ ...form, title: e.target.value })}
                      />

                      <Input
                        placeholder="Artist"
                        value={form.artist}
                        onChange={e => setForm({ ...form, artist: e.target.value })}
                      />

                      <div>
                        <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                          Genre
                        </label>

                        <Select
                          value={form.genre || undefined}
                          onValueChange={value => setForm(prev => ({
                            ...prev,
                            genre: value,
                            subgenre: '',
                            microgenre: '',
                          }))}
                        >
                          <SelectTrigger className="mt-2 bg-zinc-950 border-zinc-800">
                            <SelectValue placeholder="Select genre" />
                          </SelectTrigger>

                          <SelectContent>
                            {taxonomy.genres.map(g => (
                              <SelectItem key={g} value={g}>
                                {formatTaxonomyLabel(g)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                          Subgenre <span className="text-zinc-700">(optional)</span>
                        </label>

                        <Select
                          value={form.subgenre || undefined}
                          onValueChange={value => setForm(prev => ({
                            ...prev,
                            subgenre: value === '__none__' ? '' : value,
                            microgenre: '',
                          }))}
                          disabled={!form.genre}
                        >
                          <SelectTrigger className="mt-2 bg-zinc-950 border-zinc-800">
                            <SelectValue placeholder={form.genre ? 'Select subgenre' : 'Select genre first'} />
                          </SelectTrigger>

                          <SelectContent>
                            <SelectItem value="__none__">No subgenre</SelectItem>
                            {(taxonomy.subgenres_by_genre?.[form.genre] || []).map(sg => (
                              <SelectItem key={sg} value={sg}>
                                {formatTaxonomyLabel(sg)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                          Microgenre <span className="text-zinc-700">(optional)</span>
                        </label>

                        <Select
                          value={form.microgenre || undefined}
                          onValueChange={value => setForm(prev => ({
                            ...prev,
                            microgenre: value === '__none__' ? '' : value,
                          }))}
                          disabled={!form.subgenre}
                        >
                          <SelectTrigger className="mt-2 bg-zinc-950 border-zinc-800">
                            <SelectValue placeholder={form.subgenre ? 'Select microgenre' : 'Select subgenre first'} />
                          </SelectTrigger>

                          <SelectContent>
                            <SelectItem value="__none__">No microgenre</SelectItem>
                            {(taxonomy.microgenres_by_genre?.[form.genre]?.[form.subgenre] || []).map(mg => (
                              <SelectItem key={mg} value={mg}>
                                {formatTaxonomyLabel(mg)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <Input
                        placeholder="Source Link"
                        value={form.source_url || ''}
                        onChange={e =>
                          setForm({
                            ...form,
                            source_url: e.target.value,
                          })
                        }
                      />

                      <div>
                        <div className="mb-2 flex items-center justify-between gap-3">
                          <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                            Lyrics
                          </label>

                          <Button
                            type="button"
                            variant="outline"
                            onClick={fetchLyrics}
                            disabled={fetchingLyrics || editingSong}
                            className="border-zinc-800"
                          >
                            {fetchingLyrics ? 'Fetching…' : 'Fetch Lyrics'}
                          </Button>
                        </div>

                        <Textarea
                          placeholder="Lyrics"
                          value={form.lyrics || ''}
                          onChange={e => setForm({ ...form, lyrics: e.target.value })}
                          className="min-h-[180px]"
                        />
                      </div>
                    </div>

                    <DialogFooter>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setShowEditSong(false)}
                        className="border-zinc-800"
                      >
                        Cancel
                      </Button>

                      <Button
                        type="button"
                        onClick={editSong}
                        disabled={editingSong}
                        className="btn-neon rounded-full"
                      >
                        {editingSong ? 'Saving…' : 'Save changes'}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            ) : (
              <div className="flex gap-2 items-center flex-wrap">
                <Select value={playlistId} onValueChange={setPlaylistId}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800 max-w-sm">
                    <SelectValue placeholder={playlists.length ? 'Pick a playlist' : 'No playlists yet'} />
                  </SelectTrigger>
                  <SelectContent>
                    {playlists.map(p => (
                      <SelectItem key={p.playlist_id} value={p.playlist_id}>
                        {p.name} — {p.track_count} tracks
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <div className="basis-full h-0" />

                <Dialog open={showAddPlaylist} onOpenChange={setShowAddPlaylist}>
                  <DialogTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      className="border-zinc-800 hover:border-zinc-600"
                    >
                      + Add playlist
                    </Button>
                  </DialogTrigger>

                  <DialogContent className="bg-zinc-950 border-zinc-800">
                    <DialogHeader>
                      <DialogTitle className="font-display">Add a playlist</DialogTitle>
                    </DialogHeader>

                    <div className="space-y-3">
                      <Input
                        placeholder="Paste a Spotify, YouTube, Yandex Music or other playlist link…"
                        value={playlistLink}
                        onChange={e => setPlaylistLink(e.target.value)}
                        className="bg-zinc-950 border-zinc-800"
                      />

                      <div className="text-xs text-zinc-500">
                        Example: https://open.spotify.com/playlist/..., https://youtube.com/playlist?list=...
                      </div>
                    </div>

                    <DialogFooter>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setShowAddPlaylist(false)}
                        className="border-zinc-800"
                      >
                        Cancel
                      </Button>

                      <Button
                        type="button"
                        onClick={addPlaylist}
                        disabled={addingPlaylist || !playlistLink.trim()}
                        className="btn-neon rounded-full"
                      >
                        {addingPlaylist ? 'Adding…' : 'Add playlist'}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>

                {playlistId && (
                  <>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        const playlist = playlists.find(p => p.playlist_id === playlistId);
                        if (playlist) openEditPlaylist(playlist);
                      }}
                      className="border-zinc-800 hover:border-[#ccff00]/60"
                    >
                      Edit
                    </Button>

                    <Button
                      type="button"
                      variant="outline"
                      disabled={refreshingPlaylist}
                      onClick={() => {
                        const playlist = playlists.find(
                          p => p.playlist_id === playlistId
                        );
                        if (playlist) refreshPlaylist(playlist);
                      }}
                      className="border-zinc-800 hover:border-[#ccff00]/60"
                    >
                      {refreshingPlaylist ? 'Refreshing…' : 'Refresh playlist'}
                    </Button>

                    <Button
                      type="button"
                      variant="outline"
                      disabled={fetchingPlaylistLyrics}
                      onClick={() => {
                        const playlist = playlists.find(
                          p => p.playlist_id === playlistId
                        );
                        if (playlist) getPlaylistLyrics(playlist);
                      }}
                      className="border-zinc-800 hover:border-[#ccff00]/60"
                    >
                      {fetchingPlaylistLyrics ? 'Getting lyrics…' : 'Get Lyrics'}
                    </Button>

                    <Button
                      type="button"
                      variant="outline"
                      onClick={async () => {
                        const playlist = playlists.find(p => p.playlist_id === playlistId);
                        if (!playlist) return;

                        const confirmed = window.confirm(
                          `Remove "${playlist.name}" from your TuneAmplifier catalog?`
                        );

                        if (!confirmed) return;

                        try {
                          await api.delete(`/playlists/${playlist.playlist_id}`);
                          setPlaylistId('');
                          await load();
                          toast.success(`Removed "${playlist.name}"`);
                        } catch (e) {
                          toast.error(e.response?.data?.detail || 'Could not remove playlist');
                        }
                      }}
                      className="border-red-500/40 text-red-400 hover:border-red-500/70 hover:text-red-300"
                    >
                      Remove
                    </Button>
                  </>
                )}

                <Dialog open={showEditPlaylist} onOpenChange={setShowEditPlaylist}>
                  <DialogContent className="bg-zinc-950 border-zinc-800">
                    <DialogHeader>
                      <DialogTitle className="font-display">Edit playlist</DialogTitle>
                    </DialogHeader>

                    <div className="space-y-3">
                      <Input
                        placeholder="Playlist name"
                        value={editPlaylistForm.name}
                        onChange={e => setEditPlaylistForm({
                          ...editPlaylistForm,
                          name: e.target.value,
                        })}
                      />

                      <Input
                        placeholder="Source Link"
                        value={editPlaylistForm.source_url || ''}
                        onChange={e =>
                          setEditPlaylistForm({
                            ...editPlaylistForm,
                            source_url: e.target.value,
                          })
                        }
                      />
                    </div>

                    <DialogFooter>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setShowEditPlaylist(false)}
                        className="border-zinc-800"
                      >
                        Cancel
                      </Button>

                      <Button
                        type="button"
                        onClick={editPlaylist}
                        disabled={editingPlaylist}
                        className="btn-neon rounded-full"
                      >
                        {editingPlaylist ? 'Saving…' : 'Save changes'}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            )}
          </div>
        </Step>

        {/* Step 2 — captions */}
        <Step n={2} title="Generate a caption per platform">
          <div className="grid lg:grid-cols-5 gap-4">
            <div className="lg:col-span-2 space-y-3">
              <div className="grid grid-cols-2 gap-2">
                {PLATFORMS.map(p => {
                  const Icon = p.icon; const on = platforms.includes(p.id);
                  return (
                    <button key={p.id} data-testid={`caption-platform-${p.id}`} onClick={() => togglePlatform(p.id)}
                      className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm transition-[background-color,border-color] ${on ? 'border-[#ccff00] bg-[#ccff00]/10 text-zinc-100' : 'border-zinc-800 hover:border-zinc-600 text-zinc-400'}`}>
                      <Icon size={16} /> {p.name}
                    </button>
                  );
                })}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Select value={tone} onValueChange={setTone}>
                  <SelectTrigger data-testid="create-tone-select" className="bg-zinc-950 border-zinc-800"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="energetic">Energetic</SelectItem>
                    <SelectItem value="mysterious">Mysterious</SelectItem>
                    <SelectItem value="raw">Raw / underground</SelectItem>
                    <SelectItem value="playful">Playful</SelectItem>
                    <SelectItem value="dramatic">Dramatic</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={aiModel} onValueChange={setAiModel}>
                  <SelectTrigger data-testid="create-model-select" className="bg-zinc-950 border-zinc-800"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="gpt-terra">GPT-4o</SelectItem>
                    <SelectItem value="gpt-mini">GPT-4o mini</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button data-testid="create-generate-captions-btn" onClick={generateCaptions} disabled={genCaptions} className="w-full btn-neon rounded-full font-display font-semibold h-10">
                <Sparkle size={16} weight="fill" className="mr-2" /> {genCaptions ? 'Writing…' : 'Generate captions'}
              </Button>
            </div>
            <div className="lg:col-span-3 space-y-3">
              {!captions && !genCaptions && (
                <EmptyPane text="per-platform captions will appear here" />
              )}
              {genCaptions && <SkeletonPane count={2} />}
              {captions?.results?.map((r, i) => <CaptionCard key={i} result={r} />)}
            </div>
          </div>
        </Step>

        {/* Step 3 — media */}
        <Step n={3} title="Generate a photo, video or lyrics video">
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                data-testid="create-media-mode-media"
                onClick={() => setMediaMode('media')}
                className={`px-3 py-2.5 rounded-lg border text-sm transition-[background-color,border-color] ${
                  mediaMode === 'media'
                    ? 'border-[#ccff00] bg-[#ccff00]/10 text-zinc-100'
                    : 'border-zinc-800 hover:border-zinc-600 text-zinc-400'
                }`}
              >
                Photo / Video
              </button>

              <button
                type="button"
                data-testid="create-media-mode-lyrics"
                onClick={() => setMediaMode('lyrics')}
                className={`px-3 py-2.5 rounded-lg border text-sm transition-[background-color,border-color] ${
                  mediaMode === 'lyrics'
                    ? 'border-[#ccff00] bg-[#ccff00]/10 text-zinc-100'
                    : 'border-zinc-800 hover:border-zinc-600 text-zinc-400'
                }`}
              >
                Lyrics Video
              </button>
            </div>

            {mediaMode === 'media' ? (
              <div className="grid lg:grid-cols-5 gap-4">
            <div className="lg:col-span-2 space-y-3">
              <Select value={mediaKind} onValueChange={setMediaKind}>
                <SelectTrigger data-testid="create-media-kind-select" className="bg-zinc-950 border-zinc-800"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="image">Photo</SelectItem>
                  <SelectItem value="video">Video</SelectItem>
                </SelectContent>
              </Select>

                <Select value={mediaFormat} onValueChange={setMediaFormat}>
                  <SelectTrigger
                    data-testid="create-media-format-select"
                    className="bg-zinc-950 border-zinc-800"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="square">Square · 1:1</SelectItem>
                    <SelectItem value="portrait">Portrait · 9:16</SelectItem>
                    <SelectItem value="landscape">Landscape · 16:9</SelectItem>
                  </SelectContent>
                </Select>
                {mediaKind === 'image' ? (
                  <>
                    <Select value={imgModel} onValueChange={setImgModel}>
                      <SelectTrigger
                        data-testid="create-img-model-select"
                        className="bg-zinc-950 border-zinc-800"
                      >
                        <SelectValue />
                      </SelectTrigger>

                      <SelectContent>
                        <SelectItem value="gpt-image-2.5-flare">
                          Fast · GPT Image 2.5 Flare
                        </SelectItem>

                        <SelectItem value="gpt-image-2.5-sunburst">
                          Premium · GPT Image 2.5 Sunburst
                        </SelectItem>
                      </SelectContent>
                    </Select>

                    <Select value={imgQuality} onValueChange={setImgQuality}>
                      <SelectTrigger
                        data-testid="create-img-quality-select"
                        className="bg-zinc-950 border-zinc-800"
                      >
                        <SelectValue />
                      </SelectTrigger>

                      <SelectContent>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="xhigh">Very high</SelectItem>
                        <SelectItem value="max">Maximum</SelectItem>
                        <SelectItem value="auto">Auto</SelectItem>
                      </SelectContent>
                    </Select>
                  </>
                ) : (
                <>
                  <Select value={vidModel} onValueChange={setVidModel}>
                    <SelectTrigger data-testid="create-vid-model-select" className="bg-zinc-950 border-zinc-800"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sora">Sora 2 · {pricing?.sora_2_credits_per_sec ?? 1} credits/sec</SelectItem>
                      <SelectItem value="kling">Kling 2.1 · {pricing?.kling_21_credits_per_sec ?? 3} credits/sec</SelectItem>
                    </SelectContent>
                  </Select>
                  {vidModel === 'sora' ? (
                    <Select value={String(duration)} onValueChange={v => setDuration(+v)}>
                      <SelectTrigger data-testid="create-duration-select" className="bg-zinc-950 border-zinc-800"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="4">4s · {4 * (pricing?.sora_2_credits_per_sec ?? 1)} cr</SelectItem>
                        <SelectItem value="8">8s · {8 * (pricing?.sora_2_credits_per_sec ?? 1)} cr</SelectItem>
                        <SelectItem value="12">12s · {12 * (pricing?.sora_2_credits_per_sec ?? 1)} cr</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <Select value={String(klingDuration)} onValueChange={v => setKlingDuration(+v)}>
                      <SelectTrigger data-testid="create-kling-duration-select" className="bg-zinc-950 border-zinc-800"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="5">5s · {5 * (pricing?.kling_21_credits_per_sec ?? 3)} cr</SelectItem>
                        <SelectItem value="10">10s · {10 * (pricing?.kling_21_credits_per_sec ?? 3)} cr</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </>
              )}
              <Button data-testid="create-generate-media-btn" onClick={generateMedia} disabled={genMedia || !songId} className="w-full btn-neon rounded-full font-display font-semibold h-10">
                {mediaKind === 'image' ? <ImageSquare size={16} weight="fill" className="mr-2" /> : <VideoCamera size={16} weight="fill" className="mr-2" />}
                {genMedia ? 'Generating…' : `Generate ${mediaKind}`}
              </Button>

              <div className="mt-4 rounded-xl border border-zinc-800 bg-zinc-950/60 p-4">
                {mediaKind === 'image' ? (
                  <div className="mb-3">
                    <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-2">
                      Artwork prompt
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setMediaImagePromptMode('ai')}
                        disabled={genMedia}
                        className={`h-9 rounded-full border text-xs ${
                          mediaImagePromptMode === 'ai'
                            ? 'border-[#ccff00] text-[#ccff00] bg-[#ccff00]/10'
                            : 'border-zinc-800 text-zinc-300 hover:border-[#ccff00]/60'
                        }`}
                      >
                        Use AI prompt
                      </button>

                      <button
                        type="button"
                        onClick={() => setMediaImagePromptMode('custom')}
                        disabled={genMedia}
                        className={`h-9 rounded-full border text-xs ${
                          mediaImagePromptMode === 'custom'
                            ? 'border-[#ccff00] text-[#ccff00] bg-[#ccff00]/10'
                            : 'border-zinc-800 text-zinc-300 hover:border-[#ccff00]/60'
                        }`}
                      >
                        Use manual custom prompt
                      </button>
                    </div>

                    {mediaImagePromptMode === 'ai' ? (
                      <p className="mt-2 text-[11px] text-zinc-500 leading-relaxed">
                        TuneAmplifier will use the existing AI artwork prompt and
                        song/playlist context automatically.
                      </p>
                    ) : (
                      <textarea
                        value={mediaCustomPrompt}
                        onChange={(e) => setMediaCustomPrompt(e.target.value)}
                        disabled={genMedia}
                        rows={4}
                        placeholder="Describe exactly what you want the AI artwork to look like..."
                        className="mt-2 w-full rounded-xl border border-zinc-800 bg-zinc-950/80 p-3 text-xs text-zinc-200 placeholder:text-zinc-600 outline-none focus:border-[#ccff00]/60 resize-y"
                      />
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-zinc-500 leading-relaxed mb-3">
                    Choose your artwork independently from the video.
                    TuneAmplifier can download the highest-quality available
                    artwork from the song&apos;s source link, or you can upload
                    your own artwork.
                  </p>
                )}

                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    disabled={loadingCommunityArtwork || !songId}
                    onClick={loadCommunityArtwork}
                    className="border-zinc-800 hover:border-[#ccff00]/60"
                  >
                    {loadingCommunityArtwork
                      ? 'Loading artwork…'
                      : 'Use song artwork'}
                  </Button>

                  <label
                    className={`h-10 px-4 rounded-full border border-zinc-800 hover:border-[#ccff00]/60 text-zinc-300 flex items-center justify-center text-sm cursor-pointer ${
                      uploadingCommunityArtwork ? 'opacity-50 pointer-events-none' : ''
                    }`}
                  >
                    {uploadingCommunityArtwork
                      ? 'Uploading…'
                      : 'Upload artwork'}

                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      className="hidden"
                      disabled={uploadingCommunityArtwork}
                      onChange={(e) => {
                        uploadCommunityArtwork(
                          e.target.files?.[0] || null
                        );
                        e.target.value = '';
                      }}
                    />
                  </label>
                </div>

                {communityArtworkUrl && (
                  <div className="mt-3 text-[11px] text-zinc-500">
                    {communityArtworkSource === 'song_artwork'
                      ? 'Song artwork selected from the source link.'
                      : 'Uploaded artwork selected.'}
                  </div>
                )}
              </div>

            </div>
            <div className="lg:col-span-3">
              {!media && !genMedia && <EmptyPane text="your photo or video will appear here" />}
              {genMedia && <div className="border border-zinc-800 rounded-xl bg-zinc-900/40 min-h-[220px] flex items-center justify-center">
                <div className="flex flex-col items-center gap-3">
                  <div className="w-8 h-8 rounded-full border-2 border-[#ccff00] border-t-transparent animate-spin" />
                  <p className="font-mono text-xs text-zinc-500">{mediaKind === 'video' ? 'filming (1–5 min)…' : 'painting…'}</p>
                </div>
              </div>}
              {media && (
                <div className="border border-zinc-800 rounded-xl bg-zinc-900/40 overflow-hidden">
                  {media.kind === 'image' ? (
                    <div>
                      <div className="relative flex items-center justify-center min-h-[220px] bg-black p-4">
                        <div
                          className="relative overflow-hidden bg-black select-none touch-none cursor-grab active:cursor-grabbing"
                          style={{
                            aspectRatio:
                              mediaFormat === 'portrait'
                                ? '9 / 16'
                                : mediaFormat === 'landscape'
                                  ? '16 / 9'
                                  : '1 / 1',
                            maxHeight: '520px',
                            width:
                              mediaFormat === 'portrait'
                                ? 'min(100%, 292px)'
                                : 'min(100%, 924px)',
                            margin: '0 auto',
                          }}
                          onPointerDown={handleMediaArtworkPointerDown}
                          onPointerMove={handleMediaArtworkPointerMove}
                          onPointerUp={handleMediaArtworkPointerUp}
                          onPointerCancel={handleMediaArtworkPointerUp}
                        >
                          <img
                            key={
                              mediaArtworkPreviewUrl ||
                              media.preview_url ||
                              media.url
                            }
                            src={
                              mediaArtworkPreviewUrl ||
                              media.preview_url ||
                              `${BACKEND}${media.url}`
                            }
                            alt=""
                            onLoad={(e) => {
                              setMediaArtworkNaturalSize({
                                width: e.currentTarget.naturalWidth || 1,
                                height: e.currentTarget.naturalHeight || 1,
                              });
                            }}
                            style={getMediaArtworkImageStyle()}
                            draggable={false}
                            data-testid="create-media-result-image"
                          />
                        </div>
                      </div>

                      <div className="border-t border-zinc-800 bg-zinc-950/80 p-3">
                        <div className="flex flex-wrap items-center gap-2">
                          <button
                            type="button"
                            onClick={setMediaArtworkFit}
                            className={`h-9 px-3 rounded-full border text-xs ${
                              mediaArtworkMode === 'fit'
                                ? 'border-[#ccff00] text-[#ccff00]'
                                : 'border-zinc-800 text-zinc-300 hover:border-[#ccff00]/60'
                            }`}
                          >
                            FIT
                          </button>

                          <button
                            type="button"
                            onClick={setMediaArtworkFill}
                            className={`h-9 px-3 rounded-full border text-xs ${
                              mediaArtworkMode === 'fill'
                                ? 'border-[#ccff00] text-[#ccff00]'
                                : 'border-zinc-800 text-zinc-300 hover:border-[#ccff00]/60'
                            }`}
                          >
                            FILL
                          </button>

                          <button
                            type="button"
                            onClick={setMediaArtworkCrop}
                            className={`h-9 px-3 rounded-full border text-xs ${
                              mediaArtworkMode === 'crop'
                                ? 'border-[#ccff00] text-[#ccff00]'
                                : 'border-zinc-800 text-zinc-300 hover:border-[#ccff00]/60'
                            }`}
                          >
                            CROP
                          </button>

                          <button
                            type="button"
                            onClick={resetMediaArtwork}
                            className="h-9 px-3 rounded-full border border-zinc-800 text-xs text-zinc-300 hover:border-[#ccff00]/60"
                          >
                            RESET
                          </button>

                          <span className="text-[10px] uppercase tracking-wider text-zinc-600">
                            PAN · drag image
                          </span>

                          <div className="flex items-center gap-2 min-w-[180px] flex-1">
                            <span className="text-[10px] uppercase tracking-wider text-zinc-600">
                              ZOOM
                            </span>
                            <input
                              type="range"
                              min="1"
                              max="3"
                              step="0.01"
                              value={mediaArtworkZoom}
                              onChange={(e) =>
                                setMediaArtworkZoom(Number(e.target.value))
                              }
                              className="flex-1 accent-[#ccff00]"
                            />
                            <span className="w-10 text-right text-[10px] font-mono text-zinc-500">
                              {Number(mediaArtworkZoom || 1).toFixed(2)}×
                            </span>
                          </div>
                        </div>

                        <div className="mt-2 text-[10px] font-mono text-zinc-600">
                          {mediaArtworkSource === 'song_artwork'
                            ? 'Song artwork'
                            : mediaArtworkSource === 'uploaded_artwork'
                              ? 'Uploaded artwork'
                              : 'AI generated artwork'}
                          {' · '}
                          {mediaFormat === 'portrait'
                            ? '9:16'
                            : mediaFormat === 'landscape'
                              ? '16:9'
                              : '1:1'}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <video
                      src={`${BACKEND}${media.url}`}
                      controls
                      loop
                      className="w-full max-h-[320px]"
                      data-testid="create-media-result-video"
                    />
                  )}

                  <div className="p-3 flex items-center justify-between border-t border-zinc-800 text-xs font-mono text-zinc-500">
                    <span>{media.credits_charged} credits used</span>
                    <a
                      href={`${BACKEND}${media.url}`}
                      download
                      className="text-[#ccff00] hover:underline flex items-center gap-1"
                    >
                      <Download size={12} /> Download
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
            ) : (
              <div className="grid lg:grid-cols-5 gap-4">
                <div className="lg:col-span-2 space-y-3">
                  <div className="rounded-xl border border-zinc-800 bg-zinc-950/50 p-4">
                    <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-2">
                      Lyrics video format
                    </div>

                    <Select
                      value={lyricsVideoFormat}
                      onValueChange={setLyricsVideoFormat}
                    >
                      <SelectTrigger
                        data-testid="lyrics-video-format-select"
                        className="bg-zinc-950 border-zinc-800"
                      >
                        <SelectValue />
                      </SelectTrigger>

                      <SelectContent>
                        <SelectItem value="landscape">
                          Landscape · 16:9 · 1920×1080
                        </SelectItem>
                        <SelectItem value="portrait">
                          Portrait · 9:16 · 1080×1920
                        </SelectItem>
                      </SelectContent>
                    </Select>

                    <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mt-4 mb-2">
                      Lyric visual style
                    </div>

                    <Select
                      value={lyricsVideoStyle}
                      onValueChange={setLyricsVideoStyle}
                    >
                      <SelectTrigger
                        data-testid="lyrics-video-style-select"
                        className="bg-zinc-950 border-zinc-800"
                      >
                        <SelectValue />
                      </SelectTrigger>

                      <SelectContent>
                        <SelectItem value="auto">
                          AI recommended
                        </SelectItem>
                        <SelectItem value="futuristic">
                          Futuristic
                        </SelectItem>
                        <SelectItem value="slim">
                          Slim
                        </SelectItem>
                        <SelectItem value="modern">
                          Modern
                        </SelectItem>
                        <SelectItem value="bold">
                          Bold
                        </SelectItem>
                        <SelectItem value="condensed">
                          Condensed
                        </SelectItem>
                        <SelectItem value="elegant">
                          Elegant
                        </SelectItem>
                        <SelectItem value="western">
                          Western
                        </SelectItem>
                      </SelectContent>
                    </Select>

                    <p className="mt-2 text-[11px] text-zinc-600">
                      Uses the selected song&apos;s audio and lyrics.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Button
                      type="button"
                      data-testid="create-generate-lyrics-video-btn"
                      onClick={generateLyricsVideo}
                      disabled={
                        genLyricsVideo ||
                        uploadingLyricsArtwork ||
                        lyricsAiArtworkGenerating ||
                        !songId
                      }
                      className="w-full btn-neon rounded-full font-display font-semibold h-10"
                    >
                      <MusicNote size={16} weight="fill" className="mr-2" />
                      {genLyricsVideo
                        ? 'Rendering…'
                        : 'Generate lyrics video'}
                    </Button>

                    <Button
                      type="button"
                      onClick={generateLyricsArtwork}
                      disabled={
                        lyricsAiArtworkGenerating ||
                        genLyricsVideo ||
                        !songId
                      }
                      className="w-full h-10 px-4 rounded-full border border-zinc-800 hover:border-[#ccff00]/60 text-zinc-300 bg-transparent hover:bg-transparent"
                    >
                      <ImageSquare size={16} weight="fill" className="mr-2" />
                      {lyricsAiArtworkGenerating
                        ? 'Generating…'
                        : 'Generate image'}
                    </Button>

                    <div className="mt-4 rounded-xl border border-zinc-800 bg-zinc-950/60 p-4">
                      <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-2">
                        Artwork prompt
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                        <button
                          type="button"
                          onClick={() => setLyricsImagePromptMode('ai')}
                          disabled={lyricsAiArtworkGenerating || genLyricsVideo}
                          className={`h-9 rounded-full border text-xs ${
                            lyricsImagePromptMode === 'ai'
                              ? 'border-[#ccff00] text-[#ccff00] bg-[#ccff00]/10'
                              : 'border-zinc-800 text-zinc-300 hover:border-[#ccff00]/60'
                          }`}
                        >
                          Use AI prompt
                        </button>

                        <button
                          type="button"
                          onClick={() => setLyricsImagePromptMode('custom')}
                          disabled={lyricsAiArtworkGenerating || genLyricsVideo}
                          className={`h-9 rounded-full border text-xs ${
                            lyricsImagePromptMode === 'custom'
                              ? 'border-[#ccff00] text-[#ccff00] bg-[#ccff00]/10'
                              : 'border-zinc-800 text-zinc-300 hover:border-[#ccff00]/60'
                          }`}
                        >
                          Use manual custom prompt
                        </button>
                      </div>

                      {lyricsImagePromptMode === 'ai' ? (
                        <p className="mt-2 text-[11px] text-zinc-500 leading-relaxed">
                          TuneAmplifier will use the existing AI artwork prompt and
                          song context automatically.
                        </p>
                      ) : (
                        <textarea
                          value={lyricsCustomPrompt}
                          onChange={(e) => setLyricsCustomPrompt(e.target.value)}
                          disabled={lyricsAiArtworkGenerating || genLyricsVideo}
                          rows={4}
                          placeholder="Describe exactly what you want the AI artwork to look like..."
                          className="mt-2 w-full rounded-xl border border-zinc-800 bg-zinc-950/80 p-3 text-xs text-zinc-200 placeholder:text-zinc-600 outline-none focus:border-[#ccff00]/60 resize-y"
                        />
                      )}
                    </div>

                    <Button
                      type="button"
                      onClick={useSongArtworkForLyricsVideo}
                      disabled={
                        lyricsAiArtworkGenerating ||
                        genLyricsVideo ||
                        uploadingLyricsArtwork ||
                        !songId
                      }
                      className="w-full h-10 px-4 rounded-full border border-zinc-800 hover:border-[#ccff00]/60 text-zinc-300 bg-transparent hover:bg-transparent"
                    >
                      Use song artwork
                    </Button>

                    <label
                      className={`w-full h-10 px-4 rounded-full border border-zinc-800 hover:border-[#ccff00]/60 text-zinc-300 flex items-center justify-center text-sm cursor-pointer ${
                        uploadingLyricsArtwork || genLyricsVideo
                          ? 'opacity-50 pointer-events-none'
                          : ''
                      }`}
                    >
                      {uploadingLyricsArtwork
                        ? 'Uploading…'
                        : 'Upload artwork'}

                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        className="hidden"
                        disabled={uploadingLyricsArtwork || genLyricsVideo}
                        onChange={(e) => {
                          uploadLyricsArtwork(
                            e.target.files?.[0] || null
                          );
                          e.target.value = '';
                        }}
                      />
                    </label>
                  </div>

                  <p className="mt-2 text-[11px] text-zinc-600">
                    Cost: 2 credits
                  </p>

                  {lyricsArtworkFile && (
                    <div className="text-[11px] text-zinc-500 font-mono mt-2 truncate">
                      Using temporary artwork: {lyricsArtworkFile.name}
                    </div>
                  )}
                </div>

                <div className="lg:col-span-3">
                  {!media && !genLyricsVideo && !lyricsArtworkPreviewUrl && (
                    <EmptyPane text="your lyrics video will appear here" />
                  )}

                  {lyricsArtworkPreviewUrl && (
                    <div className="border border-zinc-800 rounded-xl bg-zinc-950/80 overflow-hidden">
                      <div className="relative flex items-center justify-center min-h-[220px] bg-black">
                        <div
                          className="relative w-full overflow-hidden bg-black select-none touch-none cursor-grab active:cursor-grabbing"
                          style={{
                            aspectRatio:
                              lyricsVideoFormat === 'portrait'
                                ? '9 / 16'
                                : '16 / 9',
                            maxHeight:
                              lyricsVideoFormat === 'portrait'
                                ? '520px'
                                : '520px',
                            width:
                              lyricsVideoFormat === 'portrait'
                                ? 'min(100%, 292px)'
                                : 'min(100%, 924px)',
                            margin: '0 auto',
                          }}
                          onPointerDown={handleLyricsArtworkPointerDown}
                          onPointerMove={handleLyricsArtworkPointerMove}
                          onPointerUp={handleLyricsArtworkPointerUp}
                          onPointerCancel={handleLyricsArtworkPointerUp}
                        >
                          <img
                            src={lyricsArtworkPreviewUrl}
                            alt="Lyrics video artwork preview"
                            onLoad={(e) => {
                              setLyricsArtworkNaturalSize({
                                width: e.currentTarget.naturalWidth || 1,
                                height: e.currentTarget.naturalHeight || 1,
                              });
                            }}
                            style={getLyricsArtworkImageStyle()}
                            draggable={false}
                            data-testid="create-lyrics-artwork-preview"
                          />
                        </div>

                        <div className="mt-3 rounded-lg border border-zinc-800 bg-zinc-950/80 p-3">
                          <div className="flex flex-wrap items-center gap-2">
                            <button
                              type="button"
                              onClick={setLyricsArtworkFit}
                              disabled={genLyricsVideo}
                              className={`h-8 px-3 rounded-full border text-[11px] font-mono ${
                                lyricsArtworkMode === 'fit'
                                  ? 'border-[#ccff00] text-[#ccff00] bg-[#ccff00]/10'
                                  : 'border-zinc-700 text-zinc-400 hover:text-zinc-200'
                              }`}
                            >
                              FIT
                            </button>

                            <button
                              type="button"
                              onClick={setLyricsArtworkFill}
                              disabled={genLyricsVideo}
                              className={`h-8 px-3 rounded-full border text-[11px] font-mono ${
                                lyricsArtworkMode === 'fill'
                                  ? 'border-[#ccff00] text-[#ccff00] bg-[#ccff00]/10'
                                  : 'border-zinc-700 text-zinc-400 hover:text-zinc-200'
                              }`}
                            >
                              FILL
                            </button>

                            <button
                              type="button"
                              onClick={setLyricsArtworkCrop}
                              disabled={genLyricsVideo}
                              className={`h-8 px-3 rounded-full border text-[11px] font-mono ${
                                lyricsArtworkMode === 'crop'
                                  ? 'border-[#ccff00] text-[#ccff00] bg-[#ccff00]/10'
                                  : 'border-zinc-700 text-zinc-400 hover:text-zinc-200'
                              }`}
                            >
                              CROP
                            </button>

                            <button
                              type="button"
                              onClick={resetLyricsArtwork}
                              disabled={genLyricsVideo}
                              className="h-8 px-3 rounded-full border border-zinc-700 text-zinc-400 hover:text-zinc-200 text-[11px] font-mono"
                            >
                              RESET
                            </button>

                            <span className="text-[10px] font-mono text-zinc-500 ml-1">
                              PAN: drag
                            </span>
                          </div>

                          <div className="mt-3">
                            <div className="flex items-center justify-between text-[10px] font-mono text-zinc-500 mb-1">
                              <span>ZOOM</span>
                              <span>{Number(lyricsArtworkZoom).toFixed(2)}×</span>
                            </div>

                            <input
                              type="range"
                              min="1"
                              max="3"
                              step="0.01"
                              value={lyricsArtworkZoom}
                              disabled={genLyricsVideo}
                              onChange={(e) =>
                                setLyricsArtworkZoom(Number(e.target.value))
                              }
                              className="w-full accent-[#ccff00]"
                              aria-label="Artwork zoom"
                            />
                          </div>
                        </div>

                        {genLyricsVideo && (
                          <div className="absolute inset-0 bg-black/55 backdrop-blur-[2px] flex items-center justify-center">
                            <div className="flex flex-col items-center gap-3 px-6 text-center">
                              <div className="w-9 h-9 rounded-full border-2 border-[#ccff00] border-t-transparent animate-spin" />
                              <p className="font-mono text-xs text-zinc-200">
                                Rendering lyrics video…
                              </p>
                              <p className="font-mono text-[10px] text-zinc-500">
                                Your artwork will remain visible while the video is rendered.
                              </p>
                            </div>
                          </div>
                        )}

                        {!genLyricsVideo && uploadingLyricsArtwork && (
                          <div className="absolute bottom-3 left-3 right-3 rounded-lg bg-black/65 backdrop-blur-sm px-3 py-2 text-[10px] font-mono text-zinc-300">
                            Uploading artwork…
                          </div>
                        )}
                      </div>

                      <div className="px-3 py-2 border-t border-zinc-800 text-[10px] font-mono text-zinc-500 flex items-center justify-between gap-3">
                        <span>
                          {lyricsArtworkSource === 'ai_generated'
                            ? 'AI generated artwork'
                            : lyricsArtworkSource === 'song_artwork'
                              ? 'Song artwork'
                              : lyricsArtworkSource === 'uploaded_artwork'
                                ? 'Uploaded artwork'
                                : 'Artwork preview'}{' · '}ready for lyrics video
                        </span>

                        {lyricsArtworkPreviewUrl && (
                          <a
                            href={lyricsArtworkPreviewUrl}
                            download="lyrics-video-artwork"
                            className="text-[#ccff00] hover:underline flex items-center gap-1 shrink-0"
                          >
                            <Download size={12} /> Download
                          </a>
                        )}
                      </div>
                    </div>
                  )}

                  {genLyricsVideo && !lyricsArtworkPreviewUrl && (
                    <div className="border border-zinc-800 rounded-xl bg-zinc-900/40 min-h-[220px] flex items-center justify-center">
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-8 h-8 rounded-full border-2 border-[#ccff00] border-t-transparent animate-spin" />
                        <p className="font-mono text-xs text-zinc-500">
                          rendering lyrics video…
                        </p>
                      </div>
                    </div>
                  )}

                  {media?.kind === 'lyrics_video' && (
                    <div className="border border-zinc-800 rounded-xl bg-zinc-900/40 overflow-hidden">
                      <video
                        src={`${BACKEND}${media.url}`}
                        controls
                        loop
                        preload="metadata"
                        className="w-full max-h-[520px]"
                        data-testid="create-lyrics-video-result"
                      />

                      <div className="p-3 border-t border-zinc-800 text-xs font-mono text-zinc-500">
                        <div className="flex items-center justify-between">
                          <span>
                            {media.format === 'portrait'
                              ? 'Portrait · 9:16'
                              : 'Landscape · 16:9'}{' '}
                            · {Math.round(media.duration)}s
                          </span>

                          <button
                            type="button"
                            onClick={downloadLyricsVideo}
                            className="text-[#ccff00] hover:underline flex items-center gap-1"
                          >
                            <Download size={12} /> Download
                          </button>
                        </div>

                        {media.expires_at && (
                          <div className="mt-2 text-[10px] uppercase tracking-wider text-amber-500">
                            Temporary file · expires 15 minutes after generation
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </Step>

        {/* Step 4 — select platforms & post */}
        <Step n={4} title="Select platforms & post">
          <div className="space-y-5">

            {!media?.url ? (
              <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4">
                <div className="text-sm text-amber-400 font-display font-semibold">
                  Nothing to publish yet
                </div>
                <p className="text-xs text-zinc-400 mt-1">
                  Generate or upload a photo or video first. You can publish
                  without AI-generated captions.
                </p>
              </div>
            ) : (
              <div className="rounded-xl border border-zinc-800 bg-zinc-950/40 p-4">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">
                  Media ready
                </div>
                <div className="text-sm text-zinc-200 mt-1">
                  {media.kind === 'lyrics_video'
                    ? 'Lyrics Video'
                    : media.kind === 'video'
                      ? 'Video'
                      : media.kind === 'image'
                        ? 'Image'
                        : 'Media'}
                </div>
                <p className="text-xs text-zinc-500 mt-1">
                  The selected media will be used for publishing.
                </p>
              </div>
            )}

            <div>
              <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-2">
                Select platforms
              </div>

              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                {PLATFORMS.map((p) => {
                  const Icon = p.icon;
                  const on = targets.includes(p.id);

                  return (
                    <button
                      key={p.id}
                      type="button"
                      data-testid={`target-${p.id}`}
                      onClick={() => toggleTarget(p.id)}
                      className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm transition-[background-color,border-color] ${
                        on
                          ? 'border-[#ccff00] bg-[#ccff00]/10 text-zinc-100'
                          : 'border-zinc-800 hover:border-zinc-600 text-zinc-400'
                      }`}
                    >
                      <Icon size={16} />
                      {p.name}
                    </button>
                  );
                })}
              </div>
            </div>

            {targets.includes('youtube') && (
              <div className="rounded-xl border border-zinc-800 bg-zinc-950/50 p-4">
                <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-3">
                  YouTube
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">

                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono mb-2">
                      Title
                    </div>

                    <Input
                      value={youtubeTitle}
                      readOnly
                      className="bg-zinc-950 border-zinc-800"
                    />

                    <p className="text-[11px] text-zinc-500 mt-1">
                      Generated from artist + song title.
                    </p>
                  </div>

                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono mb-2">
                      Publishing format
                    </div>

                    <Select
                      value={youtubeFormat}
                      onValueChange={setYoutubeFormat}
                    >
                      <SelectTrigger
                        data-testid="youtube-format-select"
                        className="bg-zinc-950 border-zinc-800"
                      >
                        <SelectValue />
                      </SelectTrigger>

                      <SelectContent>
                        <SelectItem value="video">
                          Full song / Video
                        </SelectItem>
                        <SelectItem value="short">
                          Short / Promo clip
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                </div>

                {media?.kind === 'lyrics_video' && (
                  <p className="text-xs text-zinc-500 mt-3">
                    Lyrics video title will use the format:
                    <span className="text-zinc-300 ml-1">
                      Artist - Song (Lyrics Video)
                    </span>
                  </p>
                )}
              </div>
            )}

            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                  Post text
                </div>

                {activeGeneratedCaption && (
                  <span className="text-[10px] font-mono text-zinc-600">
                    AI caption available · editable
                  </span>
                )}
              </div>

              <textarea
                value={currentPostContent}
                onChange={(e) => setManualPostContent(e.target.value)}
                rows={6}
                placeholder={
                  activeGeneratedCaption
                    ? 'Edit the generated caption or replace it with your own text...'
                    : 'Optional. Write your own post text, or leave empty to publish without a caption.'
                }
                className="w-full rounded-xl bg-zinc-950 border border-zinc-800 p-4 text-sm text-zinc-200 outline-none resize-y focus:border-[#ccff00]/50"
              />

              <div className="flex items-center justify-between mt-2">
                <p className="text-xs text-zinc-500">
                  AI captions are optional. You can publish with your own text
                  or with no text.
                </p>

                {manualPostContent !== null && (
                  <button
                    type="button"
                    onClick={() => setManualPostContent(null)}
                    className="text-[11px] text-zinc-500 hover:text-zinc-300"
                  >
                    Use generated caption
                  </button>
                )}
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <Button
                data-testid="create-post-now-btn"
                onClick={postNow}
                disabled={posting || !targets.length}
                aria-disabled={posting || !targets.length}
                className="btn-neon rounded-full font-display font-semibold h-11 px-6 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <PaperPlaneRight
                  size={16}
                  weight="fill"
                  className="mr-2"
                />
                {posting ? 'Posting…' : 'Post now'}
              </Button>

              <Dialog
                open={showSchedule}
                onOpenChange={setShowSchedule}
              >
                <DialogTrigger asChild>
                  <Button
                    data-testid="create-schedule-btn"
                    disabled={posting || !targets.length}
                    variant="outline"
                    className="rounded-full font-display font-semibold h-11 px-6 border-zinc-800 hover:border-[#ccff00]/60"
                  >
                    <CalendarBlank
                      size={16}
                      className="mr-2"
                    />
                    Schedule
                  </Button>
                </DialogTrigger>

                <DialogContent className="bg-zinc-950 border-zinc-800">
                  <DialogHeader>
                    <DialogTitle className="font-display">
                      Schedule for later
                    </DialogTitle>
                  </DialogHeader>

                  <div className="flex flex-col items-center gap-3">
                    <Calendar
                      mode="single"
                      selected={schedDate}
                      onSelect={d => d && setSchedDate(d)}
                      className="text-zinc-200"
                    />

                    <Input
                      data-testid="schedule-time-input"
                      type="time"
                      value={schedTime}
                      onChange={e => setSchedTime(e.target.value)}
                      className="bg-zinc-950 border-zinc-800 w-40"
                    />

                    <p className="text-xs font-mono text-zinc-500">
                      Queues {targets.length} post
                      {targets.length > 1 ? 's' : ''} for{' '}
                      {schedDate.toDateString()} @ {schedTime}
                    </p>
                  </div>

                  <DialogFooter>
                    <Button
                      data-testid="schedule-confirm-btn"
                      onClick={schedulePosts}
                      disabled={scheduling || !media?.url}
                      className="btn-neon rounded-full"
                    >
                      {scheduling ? 'Queuing…' : 'Queue posts'}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

          </div>
        </Step>


        <Dialog
          open={showYoutubeCommunityWizard}
          onOpenChange={setShowYoutubeCommunityWizard}
        >
          <DialogContent
            className="bg-zinc-950 border-zinc-800"
            style={{
              left: '2vw',
              top: '5vh',
              transform: 'none',
              width: '44vw',
              maxWidth: '44vw',
              maxHeight: '90vh',
              overflowY: 'auto',
            }}
          >
            <DialogHeader>
              <DialogTitle className="font-display text-xl">
                YouTube Community
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-5">
              <div className="rounded-xl border border-[#ccff00]/30 bg-[#ccff00]/5 p-4">
                <div className="font-mono text-[10px] uppercase tracking-wider text-[#ccff00] mb-2">
                  Final step
                </div>

                <p className="text-sm text-zinc-300 leading-relaxed">
                  YouTube Community is open on the right.
                  TuneAmplifier prepares the post text and provides the artwork
                  for manual upload.
                </p>

                <div className="mt-3 rounded-lg border border-zinc-800 bg-zinc-950/70 p-3">
                  <p className="text-sm text-white font-semibold">
                    1. Click <span className="text-[#ccff00]">Download artwork</span>
                  </p>
                  <p className="text-sm text-white font-semibold mt-2">
                    2. In YouTube, click <span className="text-[#ccff00]">Image</span>
                    and upload the downloaded artwork
                  </p>
                  <p className="text-sm text-white font-semibold mt-2">
                    3. Click <span className="text-[#ccff00]">Copy text</span> here
                  </p>
                  <p className="text-sm text-white font-semibold mt-2">
                    4. Return to YouTube and press
                    <span className="text-[#ccff00] ml-1">Ctrl + V</span>
                  </p>
                  <p className="text-sm text-white font-semibold mt-2">
                    5. Check everything and press
                    <span className="text-[#ccff00] ml-1">Post</span> in YouTube
                  </p>
                  <p className="text-sm text-white font-semibold mt-2">
                    6. Return here and press
                    <span className="text-[#ccff00] ml-1">Close</span>
                  </p>
                </div>

                <p className="text-[11px] text-zinc-500 mt-3">
                  The artwork upload and final Post action are always made by you inside YouTube.
                </p>
              </div>

              {youtubeCommunityData?.artwork_url && (
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-2">
                    Artwork
                  </div>

                  <div className="rounded-xl overflow-hidden border border-zinc-800 bg-zinc-950">
                    <img
                      src={youtubeCommunityData.artwork_url}
                      alt="YouTube Community artwork"
                      className="w-full max-h-[320px] object-contain bg-black"
                    />
                  </div>
                </div>
              )}

              <div>
                <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-2">
                  Prepared text
                </div>

                <Textarea
                  value={youtubeCommunityData?.text || ''}
                  readOnly
                  className="min-h-[150px] bg-zinc-950 border-zinc-800 text-zinc-200"
                />

                {youtubeCommunityData?.song_link && (
                  <p className="text-[11px] text-zinc-500 mt-2 break-all">
                    Song link: {youtubeCommunityData.song_link}
                  </p>
                )}
              </div>

              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  onClick={downloadYoutubeCommunityArtwork}
                  className="btn-neon rounded-full"
                >
                  Download artwork
                </Button>

                <Button
                  type="button"
                  variant="outline"
                  onClick={copyYoutubeCommunityText}
                  className="border-zinc-800 hover:border-[#ccff00]/60"
                >
                  Copy text
                </Button>

                <Button
                  type="button"
                  onClick={() => {
                    try {
                      window.focus();
                    } catch (e) {
                      // Ignore.
                    }
                    setShowYoutubeCommunityWizard(false);
                    setYoutubeCommunityData(null);
                  }}
                  className="btn-neon rounded-full ml-auto"
                >
                  Close
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {lyricsAudioUploadDialog}
      </div>
    </div>
  );
}

function Step({ n, title, children }) {
  return (
    <div className="border border-zinc-800 rounded-xl bg-zinc-900/40 p-6">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-7 h-7 rounded-full border border-[#ccff00]/40 text-[#ccff00] font-display text-sm flex items-center justify-center shrink-0">{n}</div>
        <h2 className="font-display text-lg">{title}</h2>
      </div>
      {children}
    </div>
  );
}

function EmptyPane({ text }) {
  return (
    <div className="border border-dashed border-zinc-800 rounded-xl p-10 text-center bg-zinc-900/20 h-full flex flex-col items-center justify-center min-h-[160px]">
      <MusicNote size={26} weight="duotone" className="text-zinc-700" />
      <p className="mt-3 text-xs text-zinc-500 font-mono">{text}</p>
    </div>
  );
}

function SkeletonPane({ count }) {
  return (
    <div className="space-y-3 stagger-in">
      {Array.from({ length: count }).map((_, i) => <div key={i} className="h-28 rounded-xl bg-zinc-900/50 animate-pulse border border-zinc-800" />)}
    </div>
  );
}

function CaptionCard({ result }) {
  const P = PLATFORMS.find(p => p.id === result.platform.toLowerCase()) || PLATFORMS[0];
  const Icon = P.icon;
  const copy = () => {
    const t = `${result.caption}\n\n${(result.hashtags || []).map(h => h.startsWith('#') ? h : '#' + h).join(' ')}`;
    navigator.clipboard.writeText(t);
    toast.success(`Copied ${P.name} draft`);
  };
  return (
    <div data-testid={`caption-result-${result.platform.toLowerCase()}`} className="border border-zinc-800 rounded-xl p-4 bg-zinc-900/40">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2"><Icon size={16} className="text-[#ccff00]" /><span className="font-display text-sm">{P.name}</span></div>
        <button onClick={copy} className="text-xs font-mono text-zinc-400 hover:text-[#ccff00] flex items-center gap-1 transition-colors"><Copy size={12} /> Copy</button>
      </div>
      <p className="text-sm text-zinc-100 leading-relaxed whitespace-pre-wrap">{result.caption}</p>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {(result.hashtags || []).map((h, i) => (
          <span key={i} className="text-xs font-mono text-[#ccff00] border border-[#ccff00]/30 px-2 py-0.5 rounded">{h.startsWith('#') ? h : `#${h}`}</span>
        ))}
      </div>
    </div>
  );
}
